import { useState, useEffect, useMemo } from "react";

import { useParams, useLocation, Link } from "wouter";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useToast } from "@/hooks/use-toast";

import { useAppStore } from "@/lib/store";



// Helper function to handle field compatibility between old and new field names

const getFieldValue = (obj: any, newField: string, oldField: string) => {

  return obj[newField] || obj[oldField];

};

import { Button } from "@/components/ui/button";

import { FolderTreeView } from "@/components/packages/FolderTreeView";

import HierarchicalFileSelector from "@/components/files/HierarchicalFileSelector";

import { PackageActivityCard } from "@/components/package/PackageActivityCard";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Textarea } from "@/components/ui/textarea";

import { Badge } from "@/components/ui/badge";

import { Checkbox } from "@/components/ui/checkbox";

import { getStatusColor } from '../lib/statusColors';

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogFooter,

  DialogHeader,

  DialogTitle,

} from "@/components/ui/dialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {

  DropdownMenu,

  DropdownMenuContent,

  DropdownMenuItem,

  DropdownMenuTrigger,

} from "@/components/ui/dropdown-menu";

import {

  Table,

  TableBody,

  TableCell,

  TableHead,

  TableHeader,

  TableRow,

} from "@/components/ui/table";

import { ArrowLeft, Plus, MoreHorizontal, Download, RefreshCw, FileText, Folder, FolderOpen, Trash2, Send, Building, Lock, Eye, Upload, CheckCircle, Clock, BarChart3, Calendar, Package, FileSpreadsheet, Share2, Copy, XCircle, X, User, Award, ChevronDown, ChevronRight, ChevronUp, Search, AlertTriangle, MessageSquare, Loader2, Mail, Activity, RotateCcw } from "lucide-react";

import { apiRequest } from "@/lib/queryClient";

import { TenderSubmission } from "../../../shared/types";

import { useUrlProject } from "@/hooks/useUrlProject";

import { useSidebarLayout } from "@/hooks/useSidebarLayout";



// Addendum Status Card Component

function AddendumStatusCard({ packageId }: { packageId: string }) {

  // For now, get all packages and filter client-side for addendums

  const { urlProjectId } = useUrlProject();

  const { data: allPackages, isLoading } = useQuery({

    queryKey: ['tender-packages', urlProjectId],

    queryFn: () => apiRequest(`/api/tender-packages${urlProjectId ? `?projectId=${urlProjectId}` : ''}`),

    enabled: !!urlProjectId,

  });



  if (isLoading) {

    return (

      <Card className="border-gray-200 shadow-sm">

        <CardHeader className="pb-3">

          <CardTitle className="text-lg font-semibold text-gray-900">Related Addendums</CardTitle>

        </CardHeader>

        <CardContent>

          <div className="animate-pulse space-y-2">

            <div className="h-4 bg-gray-200 rounded w-3/4"></div>

            <div className="h-4 bg-gray-200 rounded w-1/2"></div>

          </div>

        </CardContent>

      </Card>

    );

  }



  // Filter to find addendums that match this package's number prefix

  const currentPackage = Array.isArray(allPackages) ? allPackages.find((p: any) => p.id === packageId) : null;

  const addendumList = Array.isArray(allPackages) 

    ? allPackages.filter((pkg: any) => 

        pkg.isAddendum && 

        currentPackage && 

        pkg.packageNumber?.startsWith(currentPackage.packageNumber?.split('-')[0])

      ) 

    : [];



  if (addendumList.length === 0) return null;



  return (

    <Card className="border-purple-200 bg-purple-50 shadow-sm">

      <CardHeader className="pb-3">

        <CardTitle className="flex items-center gap-2 text-lg font-semibold text-purple-900">

          <Package className="w-5 h-5" />

          Related Addendums ({addendumList.length})

        </CardTitle>

      </CardHeader>

      <CardContent className="space-y-3">

        {addendumList.map((addendum: any) => (

          <div key={addendum.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-md border border-purple-200">

            <div className="flex items-center space-x-2">

              <div className={`w-2 h-2 rounded-full ${addendum.submissionStatus === 'sent' ? 'bg-green-500' : 'bg-gray-400'}`}></div>

              <span className="text-sm font-medium text-gray-900">{addendum.packageNumber}</span>

              <span className="text-xs text-gray-600">• {addendum.name}</span>

            </div>

            <div className="flex items-center space-x-2">

              <span className={`text-xs px-2 py-1 rounded ${

                addendum.submissionStatus === 'sent' 

                  ? 'bg-green-100 text-green-700' 

                  : 'bg-gray-100 text-gray-600'

              }`}>

                {addendum.submissionStatus === 'sent' ? 'Sent' : 'Draft'}

              </span>

              <Button

                variant="ghost"

                size="sm"

                onClick={() => {

                  const currentLocation = window.location.pathname;

                  const projectIdMatch = currentLocation.match(/\/projects\/([^\/]+)/);

                  const currentProjectId = projectIdMatch ? projectIdMatch[1] : '';

                  window.location.href = `/tender-packages/projects/${currentProjectId}/${addendum.id}`;

                }}

                className="text-purple-600 hover:text-purple-700 hover:bg-purple-100 p-1 h-6 w-6"

              >

                <Eye className="w-3 h-3" />

              </Button>

            </div>

          </div>

        ))}

      </CardContent>

    </Card>

  );

}



interface FileItem {

  id: string;

  name: string;

  type: string;

  path: string;

  size: number;

  lastUpdated: string;

  updatedBy: string;

  reviewStatus: string;

  originator: string;

  revision: string;

  accUrl: string;

  versionId?: string;

  currentVersionId?: string;

  isOutdated?: boolean;

  latestVersion?: string;

  versionStatus?: 'checking' | 'current' | 'outdated' | 'error';

}



interface PackageItem {

  id: string;

  itemId: string; // Autodesk item ID (URN format)

  name: string;

  type: 'file' | 'folder';

  description?: string;

  size?: number;

  lastUpdated: string;

  updatedBy: string;

  reviewStatus: 'in_review' | 'approved' | 'rejected' | 'pending';

  originator: string;

  revision?: string;

  accUrl?: string;

  versionId?: string;

  isOutdated?: boolean;

  path?: string;

  // New fields for enhanced metadata

  version?: string;

  latest_version?: string;

  added_by?: string;

  created_by?: string;

  folder_id?: string;

  version_label?: string;

  revision_attribute?: string;

  packageName?: string;

  package_number?: string;

  acc_project_id?: string;

  acc_hub_id?: string;

  acc_account_id?: string;

}



interface PackageDetails {

  id: string;

  name: string;

  description?: string;

  versionType: string;

  status: string;

  submissionStatus: string;

  createdAt: string;

  updatedAt: string;

  createdBy: string;

  lastUpdatedBy: string;

  items: PackageItem[];

  totalFiles: number;

  packageNumber?: string;

  isLocked?: boolean;

  lockedAt?: string;

  lockedBy?: string;

  isAddendum?: boolean;

  parentPackageId?: string;

  submissionDeadline?: string;

  addendums?: PackageDetails[]; // Array of addendum packages

  mainPackage?: PackageDetails; // Main package info for addendums

  projectId?: string; // Project ID for this package

  acc_project_id?: string; // ACC Project ID for this package

}



interface AvailableFile {

  id: string;

  name: string;

  type: string;

  size: number;

  path: string[];

  lastModified: string;

  hasChildren?: boolean;

}



interface ProjectFilesResponse {

  items?: AvailableFile[];

}



// TenderSubmission type is now imported from shared schema



// Helper function to convert PackageItem to FileItem for compatibility

const convertPackageItemToFileItem = (item: PackageItem): FileItem => {

  const converted = {

  id: item.id,

  name: item.name,

  type: item.type,

  path: item.path || item.description || '', // 修复：优先使用path字段，fallback到description

  size: item.size || 0,

  lastUpdated: item.lastUpdated,

  updatedBy: item.updatedBy,

  reviewStatus: item.reviewStatus,

  originator: item.originator,

  revision: item.revision || '',

  accUrl: item.accUrl || '',

  versionId: item.versionId,

  isOutdated: item.isOutdated,

  // New fields for enhanced metadata

  version: item.version,

  latest_version: item.latest_version,

  added_by: item.added_by,

  created_by: item.created_by,

  folder_id: item.folder_id,

  version_label: item.version_label,

  revision_attribute: item.revision_attribute,

  packageName: item.packageName,

  package_number: item.package_number,

  acc_project_id: item.acc_project_id,

  acc_hub_id: item.acc_hub_id,

  acc_account_id: item.acc_account_id,

  };

  

  

  return converted;

};



// TenderReturnsTab Component  

function TenderReturnsTab({ packageId, addendumId, packageSubmissions, processedAwards, tenderSettings }: { 

  packageId: string, 

  addendumId?: string,

  packageSubmissions: any[],

  processedAwards: { awards: any[], hasMultipleAwardsWarning: boolean, warningMessage?: string },

  tenderSettings: any

}) {

  const [selectedReturn, setSelectedReturn] = useState<any | null>(null);

  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const [searchFilter, setSearchFilter] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");

  const [showFilters, setShowFilters] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);

  const [activeOperation, setActiveOperation] = useState<'awarding' | 'rejecting' | 'resetting' | null>(null);

  const queryClient = useQueryClient();

  const { toast } = useToast();



  // Award mutation

  const awardMutation = useMutation({

    mutationFn: async ({ submissionId }: { submissionId: string }) => {

      setActiveOperation('awarding');

      const response = await fetch(`/api/tender-returns/${submissionId}/status`, {

        method: 'PUT',

        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ status: 'awarded' }),

      });

      

      if (!response.ok) {

        throw new Error('Failed to award tender');

      }

      

      return response.json();

    },

    onSuccess: (data, variables) => {

      // Close the popup immediately

      setShowDetailDialog(false);

      setActiveOperation(null);

      

      // Refetch the package returns data and package data
      
      queryClient.invalidateQueries({ queryKey: ['package-returns', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package-submissions', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId, null] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId, addendumId] });
      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });
      

      toast({

        title: "Success",

        description: "Tender has been awarded successfully",

      });

    },

    onError: () => {

      setActiveOperation(null);

      toast({

        title: "Error",

        description: "Failed to award tender. Please try again.",

        variant: "destructive",

      });

    },

  });



  // Reject mutation

  const rejectMutation = useMutation({

    mutationFn: async ({ submissionId }: { submissionId: string }) => {

      setActiveOperation('rejecting');

      const response = await fetch(`/api/tender-returns/${submissionId}/status`, {

        method: 'PUT',

        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ status: 'rejected' }),

      });

      

      if (!response.ok) {

        throw new Error('Failed to reject tender');

      }

      

      return response.json();

    },

    onSuccess: (data, variables) => {

      // Close the popup immediately

      setShowDetailDialog(false);

      setActiveOperation(null);

      

      // Refetch the package returns data and package data
      
      queryClient.invalidateQueries({ queryKey: ['package-returns', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package-submissions', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId, null] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId, addendumId] });
      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });
      

      

      toast({

        title: "Success",

        description: "Tender has been rejected successfully",

      });

    },

    onError: () => {

      setActiveOperation(null);

      toast({

        title: "Error",

        description: "Failed to reject tender. Please try again.",

        variant: "destructive",

      });

    },

  });



  // Reset mutation

  const resetMutation = useMutation({

    mutationFn: async ({ submissionId }: { submissionId: string }) => {

      setActiveOperation('resetting');

      const response = await fetch(`/api/tender-submissions/${submissionId}/reset-award`, {

        method: 'POST',

        headers: { 'Content-Type': 'application/json' },

      });

      

      if (!response.ok) {

        throw new Error('Failed to reset tender award');

      }

      

      return response.json();

    },

    onSuccess: (data, variables) => {

      // Close the popup immediately

      setShowDetailDialog(false);

      setActiveOperation(null);

      

      // Refetch the package returns data and package data
      
      queryClient.invalidateQueries({ queryKey: ['package-returns', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package-submissions', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId, null] });
      queryClient.invalidateQueries({ queryKey: ['package', packageId, addendumId] });
      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });
      

      

      toast({

        title: "Success",

        description: "Tender award has been reset successfully",

      });

    },

    onError: () => {

      setActiveOperation(null);

      toast({

        title: "Error",

        description: "Failed to reset tender award. Please try again.",

        variant: "destructive",

      });

    },

  });



  const updateReturnStatus = (submissionId: string, status: string) => {

    // Prevent multiple operations at the same time

    if (activeOperation !== null) {

      return;

    }

    

    if (status === 'awarded') {

      awardMutation.mutate({ submissionId });

    } else if (status === 'rejected') {

      rejectMutation.mutate({ submissionId });

    } else if (status === 'tender_returned') {

      resetMutation.mutate({ submissionId });

    }

  };

  

  const { data: packageReturns = [], isLoading, error, refetch } = useQuery<any[]>({

    queryKey: ['package-returns', packageId],

    queryFn: async (): Promise<any[]> => {
      const result = await apiRequest(`/api/tender-packages/${packageId}/returns`);
      return Array.isArray(result) ? result : [];

    },

    staleTime: 0, // Always consider data stale to force refetch on invalidation

    gcTime: 10 * 60 * 1000, // Cache for 10 minutes

    refetchOnWindowFocus: false, // Don't refetch on window focus

  });



  const formatDate = (dateString: string) => {

    return new Date(dateString).toLocaleDateString('en-US', {

      year: 'numeric',

      month: 'short',

      day: 'numeric',

      hour: '2-digit',

      minute: '2-digit'

    });

  };



  // Filter tender returns based on search and status filters

  const filteredReturns = packageReturns?.filter((returnItem: any) => {

    // Search filter

    if (searchFilter.trim()) {

      const matchesSearch = returnItem.recipientCompany?.toLowerCase().includes(searchFilter.toLowerCase()) ||

                           returnItem.recipientEmail?.toLowerCase().includes(searchFilter.toLowerCase()) ||

                           returnItem.recipientName?.toLowerCase().includes(searchFilter.toLowerCase());

      if (!matchesSearch) return false;

    }

    

    // Status filter

    if (statusFilter !== "all" && returnItem.status !== statusFilter) {

      return false;

    }

    

    return true;

  }) || [];









  return (

    <div className="relative">

      

      <Card className="border-gray-200 shadow-sm">

      <CardHeader>

        <div className="flex items-center justify-between">

          <CardTitle className="flex items-center text-lg font-semibold text-gray-900">

            <Upload className="w-5 h-5 mr-2 text-green-600" />

            Tender Returns & Responses

            {packageReturns.length > 0 && (

              <span className="ml-2 text-sm font-normal text-gray-500">({packageReturns.length})</span>

            )}

          </CardTitle>

          <div className="flex items-center space-x-4">

            <Input

              placeholder="Search by company or email..."

              value={searchFilter}

              onChange={(e) => setSearchFilter(e.target.value)}

              className="w-64 h-9 text-sm"

            />

            <select

              value={statusFilter}

              onChange={(e) => setStatusFilter(e.target.value)}

              className="h-9 text-sm border border-gray-200 rounded-md px-3 focus:border-blue-300 focus:ring-blue-200 focus:ring-1 focus:outline-none"

            >

              <option value="all">All Status</option>

              <option value="tender_returned">Returned</option>

              <option value="awarded">Awarded</option>

              <option value="rejected">Rejected</option>

            </select>

            <Button 

              variant="outline" 

              size="sm" 

              onClick={() => {

                queryClient.invalidateQueries({ queryKey: ['package-returns', packageId] });

              }}

              disabled={isLoading}

            >

              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />

              Refresh

            </Button>

          </div>

        </div>

      </CardHeader>

      <CardContent>

        {/* Multiple Awards Warning */}

        {processedAwards.hasMultipleAwardsWarning && (

          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">

            <div className="flex items-start">

              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />

              <div>

                <h4 className="text-sm font-medium text-yellow-800">Multiple Awards Warning</h4>

                <p className="text-sm text-yellow-700 mt-1">{processedAwards.warningMessage}</p>

                <p className="text-xs text-yellow-600 mt-2">

                  Found {processedAwards.awards.length} awards for this package. 

                  Only the earliest award should be considered valid.

                </p>

              </div>

            </div>

          </div>

        )}

        

        {isLoading ? (

          <div className="text-center py-8">

            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>

            <p className="mt-2 text-gray-600">Loading tender returns...</p>

          </div>

        ) : error ? (

          <div className="text-center py-8 text-red-600">

            <XCircle className="w-8 h-8 mx-auto mb-2" />

            <p>Failed to load tender returns</p>

          </div>

        ) : packageReturns.length === 0 ? (

          <div className="text-center py-12">

            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />

            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Returns Yet</h3>

            <p className="text-gray-500 mb-4">Companies haven't submitted their tender responses yet.</p>

            {(() => {
              // Filter submissions that have actually sent messages
              const validSubmissions = packageSubmissions.filter((sub: any) => {
                const message = sub.messageToRecipients;
                return message && 
                       message.trim() !== '' && 
                       message.trim().toLowerCase() !== 'no message provided';
              });
              
              return validSubmissions.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                  <p className="text-sm text-blue-800">
                    Submitted to {new Set(validSubmissions.map(s => s.recipientCompany)).size} companies. Returns will appear here when companies respond to the tender.
                  </p>
                </div>
              );
            })()}

          </div>

        ) : (

          <div className="space-y-4">



            <div className="overflow-x-auto">

              <Table className="min-w-[800px]">

                <TableHeader>

                  <TableRow>

                    <TableHead className="w-[20%] min-w-[120px]">Company</TableHead>

                    <TableHead className="w-[15%] min-w-[100px]">Submitted By</TableHead>

                    <TableHead className="w-[12%] min-w-[100px] text-center">Submitted Date</TableHead>

                    <TableHead className="w-[15%] min-w-[80px] text-center">Award Status</TableHead>

                    <TableHead className="w-[8%] min-w-[60px] text-center">Files</TableHead>

                    <TableHead className="w-[30%] min-w-[150px]">Notes</TableHead>

                  </TableRow>

                </TableHeader>

              <TableBody>

                {filteredReturns.length === 0 ? (

                  <TableRow>

                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">

                      No tender returns match your current filters.

                    </TableCell>

                  </TableRow>

                ) : (

                  filteredReturns.map((returnItem: any) => (

                  <TableRow 

                    key={returnItem.id} 

                    className="hover:bg-gray-50"

                  >

                    <TableCell 

                      className="cursor-pointer"

                      onClick={() => {

                        setSelectedReturn(returnItem);

                        setShowDetailDialog(true);

                      }}

                    >

                      <div className="flex items-center gap-2">

                        <Building className="w-4 h-4 text-gray-500" />

                        <span className="font-medium text-gray-900">

                          {returnItem.recipientCompany}

                        </span>

                      </div>

                    </TableCell>

                    <TableCell 

                      className="cursor-pointer"

                      onClick={() => {

                        setSelectedReturn(returnItem);

                        setShowDetailDialog(true);

                      }}

                    >

                      <div className="flex items-center gap-2 w-full max-w-[100px]">

                        <User className="w-4 h-4 text-gray-500 flex-shrink-0" />

                        <div className="min-w-0 flex-1">

                          <div className="font-medium text-gray-900 text-sm truncate" title={returnItem.recipientName}>

                            {returnItem.recipientName}

                          </div>

                          <div className="text-xs text-gray-500 truncate" title={returnItem.recipientEmail}>

                            {returnItem.recipientEmail}

                          </div>

                        </div>

                      </div>

                    </TableCell>

                    <TableCell 

                      className="cursor-pointer text-center"

                      onClick={() => {

                        setSelectedReturn(returnItem);

                        setShowDetailDialog(true);

                      }}

                    >

                      <div className="flex items-center justify-center gap-2 text-sm text-gray-600">

                        <Calendar className="w-4 h-4" />

                        {formatDate(returnItem.createdAt)}

                      </div>

                    </TableCell>

                    <TableCell className="text-center">

                      <Button

                        variant="outline"

                        size="sm"

                        className={`h-8 px-3 text-xs cursor-pointer transition-all hover:scale-105 ${

                          returnItem.status === 'awarded' 

                            ? 'border-green-500 text-green-600 hover:bg-green-50' 

                            : returnItem.status === 'rejected' 

                            ? 'border-red-500 text-red-600 hover:bg-red-50'

                            : 'border-gray-400 text-gray-600 hover:bg-gray-50'

                        }`}

                        onClick={() => {

                          setSelectedReturn(returnItem);

                          setShowDetailDialog(true);

                        }}

                      >

                        {returnItem.status === 'awarded' ? (

                          <>

                            <Award className="w-3 h-3 mr-1" />

                            Awarded

                          </>

                        ) : returnItem.status === 'rejected' ? (

                          <>

                            <XCircle className="w-3 h-3 mr-1" />

                            Not Awarded

                          </>

                        ) : (

                          <>

                            <Clock className="w-3 h-3 mr-1" />

                            Not Reviewed

                          </>

                        )}

                      </Button>

                    </TableCell>

                    <TableCell 

                      className="text-center cursor-pointer"

                      onClick={() => {

                        setSelectedReturn(returnItem);

                        setShowDetailDialog(true);

                      }}

                    >

                      <div className="flex items-center justify-center gap-1">

                        <FileText className="w-4 h-4 text-gray-500" />

                        <span className="text-sm font-medium">

                          {returnItem.filesUploaded || 0}

                        </span>

                      </div>

                    </TableCell>

                    <TableCell 

                      className="cursor-pointer"

                      onClick={() => {

                        setSelectedReturn(returnItem);

                        setShowDetailDialog(true);

                      }}

                    >

                      <div className="text-sm text-gray-700 truncate" title={returnItem.notes || 'No additional notes'}>

                        {returnItem.notes || 'No additional notes'}

                      </div>

                    </TableCell>

                  </TableRow>

                  ))

                )}

              </TableBody>

              </Table>

            </div>

          </div>

        )}

        

        {/* Return Detail Dialog */}

        {selectedReturn && (

          <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>

            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-white border-gray-200 shadow-xl text-gray-900">

              <DialogHeader className="relative">

                <div className="flex items-start justify-between">

                  <div className="flex-1 pr-8">

                    <DialogTitle className="text-xl font-semibold text-gray-900">

                      Tender Return Details - {selectedReturn.recipientCompany}

                    </DialogTitle>

                    <DialogDescription className="text-gray-600">

                      Complete details of tender return submission

                    </DialogDescription>

                  </div>

                  {/* Status Badge - Dynamic styling based on status */}

                  <div className="flex-shrink-0 mr-16">

                    <Badge className={`

                      font-medium text-sm px-4 py-2 rounded-full

                      shadow-sm border

                      flex items-center gap-2

                      transition-all duration-200 hover:shadow-md

                      ${selectedReturn.status === 'awarded' 

                        ? 'bg-emerald-100 text-emerald-800 border-emerald-200' 

                        : selectedReturn.status === 'tender_returned' 

                        ? 'bg-yellow-100 text-yellow-800 border-yellow-200'

                        : selectedReturn.status === 'rejected'

                        ? 'bg-red-100 text-red-800 border-red-200'

                        : 'bg-yellow-100 text-yellow-800 border-yellow-200'

                      }

                    `}>

                      {selectedReturn.status === 'awarded' ? (

                        <Award className="w-4 h-4" />

                      ) : selectedReturn.status === 'tender_returned' ? (

                        <CheckCircle className="w-4 h-4" />

                      ) : selectedReturn.status === 'rejected' ? (

                        <X className="w-4 h-4" />

                      ) : (

                        <Clock className="w-4 h-4" />

                      )}

                      {selectedReturn.status === 'awarded' ? 'Awarded' : 

                       selectedReturn.status === 'tender_returned' ? 'Not Reviewed' :

                       selectedReturn.status === 'rejected' ? 'Rejected' : 'Pending'}

                    </Badge>

                  </div>

                </div>

              </DialogHeader>

              

              <div className="py-4 space-y-4">

                {/* Return Information */}

                <div className="bg-gray-50 border border-gray-300 rounded-lg p-6">

                  <h3 className="font-semibold text-gray-900 mb-4">Submission Information</h3>

                  

                  {/* Row 1: Tender Company | Submitted By */}

                  <div className="grid grid-cols-2 gap-6 mb-4">

                    <div>

                      <span className="font-medium text-gray-900">Tender Company:</span>

                      <span className="ml-2 text-gray-800">{selectedReturn.recipientCompany}</span>

                    </div>

                    <div>

                      <span className="font-medium text-gray-900">Submitted By:</span>

                      <span className="ml-2 text-gray-800">{selectedReturn.recipientName}</span>

                    </div>

                  </div>



                  {/* Row 2: Email | Submitted Date */}

                  <div className="grid grid-cols-2 gap-6 mb-4">

                    <div>

                      <span className="font-medium text-gray-900">Email:</span>

                      <span className="ml-2 text-gray-800">{selectedReturn.recipientEmail}</span>

                    </div>

                    <div>

                      <span className="font-medium text-gray-900">Submitted Date:</span>

                      <span className="ml-2 text-gray-800">

                        {selectedReturn.createdAt ? formatDate(selectedReturn.createdAt) : 'N/A'}

                      </span>

                    </div>

                  </div>



                  {/* Row 3: Submission Notes (full width) */}

                  <div className="mt-4">

                    <span className="font-medium text-gray-900">Submission Notes:</span>

                    <div className="mt-1 text-gray-800 bg-white p-3 rounded border border-gray-300">

                      {selectedReturn.notes || 'No notes provided'}

                    </div>

                  </div>

                </div>



                {/* Files and Actions */}

                <div className="grid grid-cols-2 gap-6">

                  <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">

                    <h4 className="font-bold text-gray-900 mb-4 text-lg">Files Uploaded</h4>

                    <div className="space-y-3">

                      <div className="flex items-center gap-3 mb-3">

                        <FileText className="w-6 h-6 text-blue-600" />

                        <span className="text-lg font-bold text-gray-900">

                          {selectedReturn.filesUploaded || 0} files

                        </span>

                      </div>

                      

                      {/* File List */}

                      {selectedReturn.filesUploaded && selectedReturn.filesUploaded > 0 ? (

                        <div className="space-y-2 max-h-40 overflow-y-auto">

                          {selectedReturn.files && selectedReturn.files.length > 0 ? (

                            selectedReturn.files.map((file: any, index: number) => (

                              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">

                                <div className="flex items-center gap-2 flex-1 min-w-0">

                                  <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />

                                  <span className="text-sm text-gray-800 truncate" title={file.originalName || file.fileName || `File ${index + 1}`}>

                                    {file.originalName || file.fileName || `File ${index + 1}`}

                                  </span>

                                </div>

                                <span className="text-xs text-gray-500 ml-2">

                                  {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : ''}

                                </span>

                              </div>

                            ))

                          ) : (

                            <div className="text-gray-500 text-sm italic">

                              {selectedReturn.filesUploaded} file(s) uploaded

                            </div>

                          )}

                        </div>

                      ) : (

                        <div className="text-gray-500 text-sm italic">No files uploaded</div>

                      )}

                    </div>

                  </div>



                  <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-sm">

                    <h4 className="font-bold text-gray-900 mb-4 text-lg">Actions</h4>

                    <div className="space-y-3">

                      {/* Download Button with Loading State */}

                      <Button

                        onClick={async () => {

                          if (!selectedReturn?.id) return;

                          // Check if there are files to download
                          const filesCount = selectedReturn.filesUploaded || selectedReturn.filesCount || 0;
                          if (filesCount === 0) {
                            toast({
                              title: "No Files Available",
                              description: "This tender return has no files to download.",
                              variant: "destructive",
                            });
                            return;
                          }

                          setIsDownloading(true);

                          try {
                            // Get current user info for authentication
                            const userResponse = await fetch('/api/auth/user');
                            const userData = await userResponse.json();
                            
                            if (!userData.user?.email) {
                              console.error('User not authenticated');
                              setIsDownloading(false);
                              return;
                            }

                            // Use submissionId if available, otherwise use id
                            const returnId = selectedReturn.submissionId || selectedReturn.id;
                            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001'}/api/tender-return-files/download-all/${returnId}?email=${encodeURIComponent(userData.user.email)}`, {
                              method: 'GET',
                            });

                            if (response.ok) {

                              const blob = await response.blob();

                              const url = window.URL.createObjectURL(blob);

                              const a = document.createElement('a');

                              a.style.display = 'none';

                              a.href = url;

                              a.download = `${selectedReturn.recipientCompany}_Tender_Return_Files.zip`;

                              document.body.appendChild(a);

                              a.click();

                              window.URL.revokeObjectURL(url);

                              document.body.removeChild(a);

                              toast({
                                title: "Download Started",
                                description: "Your files are being downloaded.",
                              });

                            } else if (response.status === 404) {

                              toast({
                                title: "Files Not Found",
                                description: "No files are available for this tender return.",
                                variant: "destructive",
                              });

                            } else {

                              console.error('Failed to download files:', response.status);
                              toast({
                                title: "Download Failed",
                                description: `Failed to download files: ${response.statusText}`,
                                variant: "destructive",
                              });

                            }

                          } catch (error) {

                            console.error('Error downloading files:', error);
                            toast({
                              title: "Download Error",
                              description: "An error occurred while downloading files.",
                              variant: "destructive",
                            });

                          } finally {

                            setIsDownloading(false);

                          }

                        }}

                        variant="outline"

                        className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold"

                        disabled={isDownloading || !selectedReturn?.filesUploaded || selectedReturn.filesUploaded === 0}

                      >

                        {isDownloading ? (

                          <>

                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>

                            Downloading...

                          </>

                        ) : (

                          <>

                            <Download className="w-4 h-4 mr-2" />

                            Download Files

                          </>

                        )}

                      </Button>



                      {/* Status Management Buttons */}

                      {selectedReturn?.status !== 'awarded' && selectedReturn?.status !== 'rejected' && (

                        <>

                          <Button

                            onClick={() => updateReturnStatus(selectedReturn?.id || '', 'awarded')}

                            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"

                            disabled={activeOperation !== null}

                          >

                            {activeOperation === 'awarding' ? (

                              <>

                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>

                                Awarding...

                              </>

                            ) : (

                              <>

                                <CheckCircle className="w-4 h-4 mr-2" />

                                Award Tender

                              </>

                            )}

                          </Button>

                          <Button

                            onClick={() => updateReturnStatus(selectedReturn?.id || '', 'rejected')}

                            variant="outline"

                            className="w-full border-red-500 text-red-600 hover:bg-red-50 font-semibold"

                            disabled={activeOperation !== null}

                          >

                            {activeOperation === 'rejecting' ? (

                              <>

                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>

                                Rejecting...

                              </>

                            ) : (

                              <>

                                <XCircle className="w-4 h-4 mr-2" />

                                Reject Tender

                              </>

                            )}

                          </Button>

                        </>

                      )}

                      {(selectedReturn?.status === 'awarded' || selectedReturn?.status === 'rejected') && (

                        <Button

                          onClick={() => updateReturnStatus(selectedReturn?.id || '', 'tender_returned')}

                          variant="outline"

                          className="w-full border-gray-500 text-gray-600 hover:bg-gray-50 font-semibold"

                          disabled={activeOperation !== null}

                        >

                          {activeOperation === 'resetting' ? (

                            <>

                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>

                              Resetting...

                            </>

                          ) : (

                            'Reset Award'

                          )}

                        </Button>

                      )}

                    </div>

                  </div>

                </div>

              </div>

            </DialogContent>

          </Dialog>

        )}



      </CardContent>

    </Card>

    </div>

  );

}



// Helper function to filter valid submissions
const getValidSubmissions = (submissions: any[], isAddendum: boolean = false) => {
  return submissions.filter((sub: any) => {
    const message = sub.messageToRecipients;
    // Include submissions that have been sent (have sentAt) or have a valid message
    const hasBeenSent = sub.sentAt && sub.sentAt !== null;
    const hasValidMessage = message && 
           message.trim() !== '' && 
           message.trim().toLowerCase() !== 'no message provided';
    
    // For addendum packages, show all recipients (including synced ones)
    if (isAddendum) {
      return true; // Show all recipients for addendum packages
    }
    
    // Always show submissions for company cards (so × button is always visible)
    // Only filter for other display purposes
    return true;
  });
};

export default function PackageDetail() {

  const { id, parentId, addendumId } = useParams<{ id?: string; parentId?: string; addendumId?: string }>();

  const { getPageHeaderClass } = useSidebarLayout();

  const [, setLocation] = useLocation();

  

  // Handle nested addendum URL structure: /projects/projectId/parentId/ad01

  const actualPackageId = parentId ? parentId : id; // For addendums, use parentId; for main packages, use id

  const isAddendumView = !!parentId && !!addendumId;

  

  const { toast } = useToast();

  const queryClient = useQueryClient();

  const { selectedProject } = useAppStore();



  // Get current user info

  const { data: user } = useQuery({

    queryKey: ['auth-user'],

    queryFn: async () => {

      const response = await apiRequest('/api/auth/user');

      return response.user;

    },

  });



  // Dialog states

  const [showAddFilesDialog, setShowAddFilesDialog] = useState(false);

  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  const [showAddendumDialog, setShowAddendumDialog] = useState(false);

  const [showAddendumActivity, setShowAddendumActivity] = useState(false);

  const [showAllReferencesDialog, setShowAllReferencesDialog] = useState(false);

  const [isPackageContentsExpanded, setIsPackageContentsExpanded] = useState(true);

  const [showAddCompanyDialog, setShowAddCompanyDialog] = useState(false);


  const [showRecipientsDialog, setShowRecipientsDialog] = useState(false);

  const [showCompanyRecipientsDialog, setShowCompanyRecipientsDialog] = useState(false);

  const [selectedCompanyForManagement, setSelectedCompanyForManagement] = useState<string | null>(null);

  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

  const [recipientToDelete, setRecipientToDelete] = useState<{ email: string; company: string } | null>(null);

  // Company access management states
  const [companyAccessStatus, setCompanyAccessStatus] = useState<Record<string, 'yes' | 'no'>>({});
  const [showRemoveCompanyDialog, setShowRemoveCompanyDialog] = useState(false);
  const [companyToRemove, setCompanyToRemove] = useState<string | null>(null);

  const [isSubmissionExpanded, setIsSubmissionExpanded] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");

  const [returnsCount, setReturnsCount] = useState(0);



  // Form states

  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

  const [selectedPackageItems, setSelectedPackageItems] = useState<string[]>([]);
  const [isDownloadingSelectedFiles, setIsDownloadingSelectedFiles] = useState(false);

  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

  const [submissionDeadline, setSubmissionDeadline] = useState('');

  const [emailSubject, setEmailSubject] = useState('');

  const [emailMessage, setEmailMessage] = useState('');

  const [newUserEmail, setNewUserEmail] = useState('');

  const [newUserName, setNewUserName] = useState('');

  const [addendumName, setAddendumName] = useState('');

  const [addendumDescription, setAddendumDescription] = useState('');

  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const [shareUrl, setShareUrl] = useState<string>("");

  

  // Company and user form states

  const [companyName, setCompanyName] = useState('');

  const [companyType, setCompanyType] = useState('');

  const [companyEmail, setCompanyEmail] = useState('');

  const [companyPhone, setCompanyPhone] = useState('');

  const [companyAddress, setCompanyAddress] = useState('');

  const [contactPerson, setContactPerson] = useState('');


  




  // Company Library states

  const [companySearchTerm, setCompanySearchTerm] = useState("");

  const [selectedRecipients, setSelectedRecipients] = useState<any[]>([]);



  // Navigation states

  const [folderPath, setFolderPath] = useState<AvailableFile[]>([]);



  // Package data query - handles both regular packages and nested addendum URLs

  const { data: packageData, isLoading: packageLoading, error: packageError } = useQuery<PackageDetails>({

    queryKey: ['package', actualPackageId, isAddendumView ? addendumId : null],

    queryFn: async () => {

      if (isAddendumView) {

        // For addendum view (/projects/projectId/parentId/ad01), get main package first

        const mainPackageData = await apiRequest(`/api/tender-packages/${actualPackageId}`) as PackageDetails;

        const addendums = mainPackageData.addendums || [];

        // Find addendum by number (ad01, ad02, etc.)

        const addendumNumber = addendumId?.replace('ad', '');

        const addendum = addendums.find((a: PackageDetails) => 

          a.packageNumber?.endsWith(`-AD${addendumNumber?.padStart(2, '0')}`)

        );

        if (!addendum) {

          throw new Error(`Addendum ${addendumId} not found`);

        }

        return addendum;

      }

      return apiRequest(`/api/tender-packages/${actualPackageId}`);

    },

    enabled: !!actualPackageId,

    staleTime: 30 * 1000, // Consider data fresh for 30 seconds

    gcTime: 5 * 60 * 1000, // Cache for 5 minutes

    refetchOnWindowFocus: false, // Don't refetch on window focus

  });



  // Get tender settings for this project

  const { data: tenderSettings, refetch: refetchTenderSettings } = useQuery({

    queryKey: ["project-tender-settings", packageData?.projectId],

    queryFn: async () => {

      if (!packageData?.projectId) return null;

      return await apiRequest(`/api/project-tender-settings/${packageData.projectId}`);

    },

    enabled: !!packageData?.projectId,

    staleTime: 0, // Always fetch fresh data

    gcTime: 0, // Don't cache

  });



  // Debug tender settings

  useEffect(() => {

    if (tenderSettings) {

      console.log('Tender settings loaded in PackageDetail:', tenderSettings);

    } else {

      console.log('Tender settings not loaded yet, packageData projectId:', packageData?.projectId);

    }

  }, [tenderSettings, packageData?.projectId]);



  // Listen for package refresh events - updated for nested URL handling

  useEffect(() => {

    const handleRefresh = () => {

      queryClient.invalidateQueries({ queryKey: ['package', actualPackageId, isAddendumView ? addendumId : null] });

      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });

    };

    

    window.addEventListener('refreshPackageData', handleRefresh);

    return () => window.removeEventListener('refreshPackageData', handleRefresh);

  }, [actualPackageId, isAddendumView, addendumId, queryClient]);



  // Reset file selector state when project changes

  useEffect(() => {

    setSelectedFiles([]);

    setFolderPath([]);

    // Close file selection dialog if open

    if (showAddFilesDialog) {

      setShowAddFilesDialog(false);

    }

  }, [selectedProject?.id]);



  // Auto-check version status when package data loads or changes

  useEffect(() => {

    if (packageData && packageData.items && packageData.items.length > 0) {

      // Check if any items have files that might need version checking

      const hasFiles = packageData.items.some(item => item.type === 'file');

      if (hasFiles) {

        // Only check version status once when package first loads, not on every data change

        const hasCheckedVersion = sessionStorage.getItem(`version-checked-${actualPackageId}`);

        if (!hasCheckedVersion) {

          checkVersionStatus();

          sessionStorage.setItem(`version-checked-${actualPackageId}`, 'true');

        }

      }

    }

  }, [actualPackageId]); // Only depend on actualPackageId, not packageData.items



  // Current包自动刷新功能

  useEffect(() => {

    if (packageData?.versionType === 'current') {

      // 每2分钟自动刷新一次Current包

      const autoRefreshInterval = setInterval(async () => {

        // 先触发版本检查，然后刷新数据

        try {

          if (actualPackageId) {

            await apiRequest(`/api/tender-packages/${actualPackageId}/check-updates`, {

              method: 'POST',

              headers: { 'Content-Type': 'application/json' }

            });

          }

        } catch (error) {

          console.error('❌ Error checking version status:', error);

        }

        

        queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

        queryClient.invalidateQueries({ queryKey: ['package-items', actualPackageId] });

      }, 120000); // 2分钟

      

      // 清理定时器

      return () => {

        clearInterval(autoRefreshInterval);

      };

    }

  }, [packageData?.versionType, actualPackageId, queryClient]);



  // Version checking function - updated for nested URL handling

  const checkVersionStatus = async () => {

    if (!actualPackageId) return;

    

    try {

      // Always use actualPackageId for version checking, not packageData.id

      const packageIdForApi = actualPackageId;

      if (!packageIdForApi) return;

      

      

      await apiRequest(`/api/tender-packages/${packageIdForApi}/check-updates`, {

        method: 'POST'

      });

      // Don't automatically refresh package data - let the user manually refresh if needed

      // This prevents the infinite loop of data refresh

    } catch (error) {

      console.error('Error checking version status:', error);

    }

  };



  // Pre-fill email subject when package data loads

  useEffect(() => {

    if (packageData && !emailSubject) {

      const packageNumber = packageData.packageNumber || `#${packageData.id?.slice(-4)}`;

      const defaultSubject = `Tender Package ${packageNumber} - ${packageData.name}`;

      setEmailSubject(defaultSubject);

    }

  }, [packageData?.id, packageData?.name, packageData?.packageNumber]); // Only depend on specific fields, not entire packageData



  // Package submissions query - updated for nested URL handling

  const { data: packageSubmissions = [] } = useQuery<TenderSubmission[]>({

    queryKey: ['package-submissions', actualPackageId, isAddendumView ? addendumId : null],

    queryFn: () => {

      // For addendums, use the addendum package ID; for main packages, use actualPackageId
      const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
      return packageIdToUse ? apiRequest(`/api/tender-packages/${packageIdToUse}/submissions`) : [];

    },

    enabled: !!actualPackageId,

    staleTime: 0, // Always consider data stale to ensure fresh data

    refetchOnWindowFocus: true, // Refetch when window gains focus

  });

  // Parent package submissions query - for addendum packages to sync with parent
  const { data: parentPackageSubmissions = [] } = useQuery<TenderSubmission[]>({
    queryKey: ['package-submissions', packageData?.parentPackageId],
    queryFn: () => {
      return packageData?.parentPackageId ? apiRequest(`/api/tender-packages/${packageData.parentPackageId}/submissions`) : [];
    },
    enabled: !!packageData?.isAddendum && !!packageData?.parentPackageId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  // Package returns query - for tracking returns count

  const { data: packageReturns = [] } = useQuery<any[]>({

    queryKey: ['package-returns', actualPackageId],

    queryFn: async (): Promise<any[]> => {

      const result = await apiRequest(`/api/tender-packages/${actualPackageId}/returns`);

      return Array.isArray(result) ? result : [];

    },

    enabled: !!actualPackageId,

    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes

    gcTime: 10 * 60 * 1000, // Cache for 10 minutes

    refetchOnWindowFocus: false, // Don't refetch on window focus

  });



  // Update returns count when packageReturns changes

  useEffect(() => {

    setReturnsCount(packageReturns.length);

  }, [packageReturns]);



  // Check if there are existing submissions for "Add More Companies" logic

  // For addendum packages, check if there are valid submissions (with actual messages)
  const hasExistingSubmissions = Array.isArray(packageSubmissions) && packageSubmissions.length > 0 && 
    (packageData?.isAddendum ? 
      getValidSubmissions(packageSubmissions, packageData?.isAddendum).length > 0 : 
      packageSubmissions.length > 0
    );

  const existingSubmissionData = hasExistingSubmissions ? (() => {
    // For addendum packages, find the submission with the most complete data
    if (packageData?.isAddendum) {
      return packageSubmissions
        .filter(sub => sub.emailSubject && sub.emailSubject !== 'null' && sub.emailSubject.trim() !== '')
        .sort((a, b) => {
          // Prioritize submissions with sentAt (actually sent)
          if (a.sentAt && !b.sentAt) return -1;
          if (!a.sentAt && b.sentAt) return 1;
          return 0;
        })[0] || packageSubmissions[0];
    }
    return packageSubmissions[0];
  })() : null;

  const existingCompanyIds = hasExistingSubmissions 

    ? packageSubmissions.map(sub => sub.recipientCompany).filter(Boolean)

    : [];



  // Tender settings query (using the one defined above)



  // Process awards based on tender settings

  const processedAwards = useMemo(() => {

    if (!packageSubmissions || !tenderSettings) return { awards: [], hasMultipleAwardsWarning: false };

    

    const awards = packageSubmissions.filter(submission => submission.companyAwarded === true);

    

    // If multiple awards are not allowed and there are multiple awards, show warning

    const hasMultipleAwardsWarning = !(tenderSettings as any).allowMultipleAwards && awards.length > 1;

    

    return {

      awards,

      hasMultipleAwardsWarning,

      warningMessage: (tenderSettings as any)?.multipleAwardsWarning || "Warning: Multiple awards detected for this package."

    };

  }, [packageSubmissions, tenderSettings]);



  // Available files query

  const currentFolderId = folderPath.length > 0 ? folderPath[folderPath.length - 1].id : 'root';

  const { data: availableFiles, isLoading: availableFilesLoading } = useQuery<AvailableFile[]>({

    queryKey: ['available-files', selectedProject?.id, currentFolderId],

    queryFn: async () => {

      if (!selectedProject?.id) return [];

      const url = currentFolderId === 'root' 

        ? `/api/project-files?projectId=${selectedProject.id}`

        : `/api/project-files?projectId=${selectedProject.id}&folderId=${currentFolderId}`;

      const response = await apiRequest(url) as ProjectFilesResponse | AvailableFile[];

      console.log('Available files response:', response);

      // Handle both direct array and items wrapper

      if (Array.isArray(response)) {

        return response;

      }

      return response?.items || [];

    },

    enabled: !!selectedProject?.id && showAddFilesDialog,

  });



  // Companies query (legacy)

  const { data: companies, isLoading: companiesLoading, refetch: refetchCompanies } = useQuery<any[]>({

    queryKey: ['companies'],

    queryFn: () => apiRequest('/api/companies?submit=true'),

    enabled: showSubmitDialog,

  });



  // Real Project Users query (ACC data)

  const { data: projectUsers = [], isLoading: projectUsersLoading } = useQuery<any[]>({

    queryKey: ["project-users", selectedProject?.id],

    queryFn: async () => {

      if (!selectedProject?.id) return [];

      return await apiRequest(`/api/autodesk/projects/${selectedProject.id}/users`);

    },

    enabled: !!selectedProject?.id && showSubmitDialog,

  });



  // Company Library query - Use project-specific companies data (same as Companies page)

  const { data: libraryCompanies = [], isLoading: libraryLoading, refetch: refetchLibrary } = useQuery<any[]>({

    queryKey: ['company-library', selectedProject?.id, companySearchTerm],

    queryFn: async () => {

      if (!selectedProject?.id) return [];

      // Use the same API as Companies page to get project-specific companies
      const response = await apiRequest(`/api/activities-data?type=companies&projectId=${selectedProject.id}`);
      
      
      // Apply search filter if provided
      if (companySearchTerm) {
        return response.filter((company: any) => 
          company.name.toLowerCase().includes(companySearchTerm.toLowerCase()) ||
          (company.type && company.type.toLowerCase().includes(companySearchTerm.toLowerCase()))
        );
      }
      
      return response;

    },

    enabled: showSubmitDialog && !!selectedProject?.id,

  });



  // Group project users by company (ACC data)

  const accCompaniesByUser = projectUsers?.reduce((acc, user) => {

    const companyId = user.companyId || 'unknown';

    const companyName = user.companyName || 'Unknown Company';

    

    if (!acc[companyId]) {

      acc[companyId] = {

        id: companyId,

        accId: companyId,

        name: companyName,

        type: 'Company',

        users: [],

        source: 'acc' // Mark as ACC source

      };

    }

    

    acc[companyId].users.push({

      ...user,

      source: 'acc' // Mark as ACC source

    });

    return acc;

  }, {} as Record<string, any>) || {};



  // Use only the filtered project-specific companies
  // This ensures we only show companies relevant to the current project
  const allCompanies = libraryCompanies.filter((company: any) => {
    // Only show companies that have users
    if (!company.users || !Array.isArray(company.users) || company.users.length === 0) {
      return false;
    }
    
    // Check if any user in this company belongs to the current project
    const hasProjectUser = company.users.some((user: any) => {
      return user.accProjectId === selectedProject?.id || 
             user.projectId === selectedProject?.id;
    });
    
    return hasProjectUser;
  });



  // Addendum loading state

  const [isLoadingAddendumData, setIsLoadingAddendumData] = useState(false);



  // Auto-select companies for addendum packages when companies data is loaded

  useEffect(() => {

    // For addendum packages, select only companies from the parent package

    if (showSubmitDialog && packageData?.isAddendum && packageData?.parentPackageId && selectedRecipients.length === 0) {                                                                            

      

      // Set default form values for addendum packages

      if (!submissionDeadline) {

        const tomorrow = new Date();

        tomorrow.setDate(tomorrow.getDate() + 7); // Default to 1 week from now

        setSubmissionDeadline(tomorrow.toISOString().split('T')[0]);

      }

      

      if (!emailSubject) {

        setEmailSubject(`Tender Package ${packageData?.packageNumber || 'AD'} - Addendum`);

      }

      

      if (!emailMessage) {

        setEmailMessage(`Please find attached the addendum package for your review and submission.`);

      }

      

      // Auto-sync addendum companies from parent package
      const syncAddendumCompanies = async () => {
        if (!packageData?.isAddendum || !packageData?.parentPackageId || !parentPackageSubmissions.length) return;
        
        try {
          // Check if this addendum package has already sent messages
          const addendumSubmissions = await apiRequest(`/api/tender-packages/${packageData.id}/submissions`);
          const hasAddendumSentMessages = addendumSubmissions && addendumSubmissions.length > 0 && 
            addendumSubmissions.some((sub: any) => {
              const message = sub.messageToRecipients;
              return message && 
                     message.trim() !== '' && 
                     message.trim().toLowerCase() !== 'no message provided';
            });
          
          if (hasAddendumSentMessages) {
            // If addendum has sent messages, sync with parent package updates
            console.log('[Addendum] Syncing with parent package updates (addendum has sent messages)');
          } else {
            // If addendum hasn't sent messages, show parent package companies for selection
            console.log('[Addendum] Showing parent package companies for recipient selection');
          }
          
          // Filter parent submissions that have actually sent messages
          const validParentSubmissions = parentPackageSubmissions.filter((sub: any) => {
            const message = sub.messageToRecipients;
            return message && 
                   message.trim() !== '' && 
                   message.trim().toLowerCase() !== 'no message provided';
          });
          
          // If parent package has sent messages, sync the companies
          if (validParentSubmissions.length > 0) {
            // Get unique companies from valid parent package submissions
            const parentCompanyNames = new Set(validParentSubmissions.map((sub: any) => sub.recipientCompany || sub.recipient_company));
            
            // Create recipients directly from valid parent submissions
            const recipientsToSelect: any[] = [];
            const companiesToSelect: string[] = [];
            
            // Group valid submissions by company
            const companySubmissions = new Map();
            validParentSubmissions.forEach((sub: any) => {
              const companyName = sub.recipientCompany || sub.recipient_company;
              if (companyName) {
                if (!companySubmissions.has(companyName)) {
                  companySubmissions.set(companyName, []);
                }
                companySubmissions.get(companyName).push(sub);
              }
            });
            
            // Create recipients for each company
            companySubmissions.forEach((subs, companyName) => {
              const companyId = `custom-${companyName}`;
              companiesToSelect.push(companyId);
              
              // Create recipients from submissions
              subs.forEach((sub: any) => {
                recipientsToSelect.push({
                  name: sub.recipientName || sub.recipient_name || 'Unknown',
                  email: sub.recipientEmail || sub.recipient_email || '',
                  companyName: companyName,
                  accessCode: sub.companyAccessCode, // Inherit access code from main package
                  jobTitle: '',
                  source: 'custom'
                });
              });
            });
            
            // Update selected companies and recipients
            if (companiesToSelect.length > 0) {
              setSelectedCompanies(companiesToSelect);
              setSelectedRecipients(recipientsToSelect);
              
              console.log('[Addendum] Auto-synced companies from parent package:', companiesToSelect.length);
            }
          }
        } catch (error) {
          console.error('Failed to auto-sync companies from parent package:', error);
        }
      };

      const selectAddendumCompanies = async () => {

        setIsLoadingAddendumData(true);

        try {
          // Check if this addendum package has already sent messages
          const addendumSubmissions = await apiRequest(`/api/tender-packages/${packageData.id}/submissions`);
          const hasAddendumSentMessages = addendumSubmissions && addendumSubmissions.length > 0 && 
            addendumSubmissions.some((sub: any) => {
              const message = sub.messageToRecipients;
              return message && 
                     message.trim() !== '' && 
                     message.trim().toLowerCase() !== 'no message provided';
            });
          
          if (hasAddendumSentMessages) {
            // If addendum has sent messages, sync with parent package updates
            console.log('[Addendum] Auto-syncing with parent package updates (addendum has sent messages)');
          } else {
            // If addendum hasn't sent messages, show parent package companies for selection
            console.log('[Addendum] Auto-selecting companies from parent package for recipient selection');
          }

          // Get parent package submissions to find which companies received the main package                                                                   

          const parentSubmissions = await apiRequest(`/api/tender-packages/${packageData.parentPackageId}/submissions`);                                          

        

          if (parentSubmissions && Array.isArray(parentSubmissions) && parentSubmissions.length > 0) {                                                            

            // Filter submissions that have actually sent messages
            const validSubmissions = parentSubmissions.filter((sub: any) => {
              const message = sub.messageToRecipients;
              return message && 
                     message.trim() !== '' && 
                     message.trim().toLowerCase() !== 'no message provided';
            });
            
            // Only sync if parent package has actually sent messages
            if (validSubmissions.length === 0) {
              console.log('[Addendum] Skipping auto-select - parent package has no actual messages sent');
              setIsLoadingAddendumData(false);
              return;
            }

            // Get unique companies from valid parent package submissions

            const parentCompanyNames = new Set(validSubmissions.map((sub: any) => sub.recipientCompany || sub.recipient_company));                               

            

            console.log('[Addendum] Parent companies found:', Array.from(parentCompanyNames));

            

            // Create recipients directly from valid parent submissions

            const recipientsToSelect: any[] = [];

            const companiesToSelect: string[] = [];

            

            // Group valid submissions by company

            const companySubmissions = new Map();

            validSubmissions.forEach((sub: any) => {

              const companyName = sub.recipientCompany || sub.recipient_company;

              if (companyName) {

                if (!companySubmissions.has(companyName)) {

                  companySubmissions.set(companyName, []);

                }

                companySubmissions.get(companyName).push(sub);

              }

            });

            

            // Create recipients for each company

            companySubmissions.forEach((subs, companyName) => {

              const companyId = `custom-${companyName}`;

              companiesToSelect.push(companyId);

              

              // Create recipients from submissions

              subs.forEach((sub: any) => {

                recipientsToSelect.push({

                  name: sub.recipientName || sub.recipient_name || 'Unknown',

                  email: sub.recipientEmail || sub.recipient_email || '',

                  companyName: companyName,

                  accessCode: sub.companyAccessCode, // Inherit access code from main package

                  jobTitle: '',

                  source: 'custom'

                });

              });

            });

            

            console.log('[Addendum] Companies to select:', companiesToSelect);

            console.log('[Addendum] Recipients to select:', recipientsToSelect);

            

            if (companiesToSelect.length > 0) {

              setSelectedCompanies(companiesToSelect);

              setSelectedRecipients(recipientsToSelect);

              console.log('[Addendum] Auto-selected companies and recipients');

              toast({

                title: "Companies Auto-Selected",

                description: `${companiesToSelect.length} companies from the main package have been auto-selected`,

              });

            }

          }

        } catch (error) {

          console.error('Failed to auto-select companies from parent package:', error);

        } finally {

          setIsLoadingAddendumData(false);

        }

      };

      selectAddendumCompanies();
    }
  }, [showSubmitDialog, packageData?.isAddendum, packageData?.parentPackageId]);

      // Auto-sync addendum companies when parent package submissions change
      useEffect(() => {
        if (packageData?.isAddendum && packageData?.parentPackageId && parentPackageSubmissions.length > 0) {
          // Auto-sync companies from parent package
          const syncAddendumCompanies = async () => {
            try {
              // Check if this addendum package has already sent messages
              const addendumSubmissions = await apiRequest(`/api/tender-packages/${packageData.id}/submissions`);
              const hasAddendumSentMessages = addendumSubmissions && addendumSubmissions.length > 0 && 
                addendumSubmissions.some((sub: any) => {
                  const message = sub.messageToRecipients;
                  return message && 
                         message.trim() !== '' && 
                         message.trim().toLowerCase() !== 'no message provided';
                });
              
              if (hasAddendumSentMessages) {
                // If addendum has sent messages, sync with parent package updates
                console.log('[Addendum] Auto-syncing with parent package updates (addendum has sent messages)');
              } else {
                // If addendum hasn't sent messages, show parent package companies for selection
                console.log('[Addendum] Auto-syncing companies from parent package for recipient selection');
              }
          
          // Filter parent submissions that have actually sent messages
          const validParentSubmissions = parentPackageSubmissions.filter((sub: any) => {
            const message = sub.messageToRecipients;
            return message && 
                   message.trim() !== '' && 
                   message.trim().toLowerCase() !== 'no message provided';
          });
          
          // Sync logic based on addendum status
          if (hasAddendumSentMessages) {
            // If addendum has sent messages, sync with parent package updates
            if (validParentSubmissions.length > 0) {
            // Get unique companies from valid parent package submissions
            const parentCompanyNames = new Set(validParentSubmissions.map((sub: any) => sub.recipientCompany || sub.recipient_company));
            
            // Create recipients directly from valid parent submissions
            const recipientsToSelect: any[] = [];
            const companiesToSelect: string[] = [];
            
            // Group valid submissions by company
            const companySubmissions = new Map();
            validParentSubmissions.forEach((sub: any) => {
              const companyName = sub.recipientCompany || sub.recipient_company;
              if (companyName) {
                if (!companySubmissions.has(companyName)) {
                  companySubmissions.set(companyName, []);
                }
                companySubmissions.get(companyName).push(sub);
              }
            });
            
            // Create recipients for each company
            companySubmissions.forEach((subs, companyName) => {
              const companyId = `custom-${companyName}`;
              companiesToSelect.push(companyId);
              
              // Create recipients from submissions
              subs.forEach((sub: any) => {
                recipientsToSelect.push({
                  name: sub.recipientName || sub.recipient_name || 'Unknown',
                  email: sub.recipientEmail || sub.recipient_email || '',
                  companyName: companyName,
                  accessCode: sub.companyAccessCode, // Inherit access code from main package
                  jobTitle: '',
                  source: 'custom'
                });
              });
            });
            
            // Update selected companies and recipients
            if (companiesToSelect.length > 0) {
              setSelectedCompanies(companiesToSelect);
              setSelectedRecipients(recipientsToSelect);
              
              console.log('[Addendum] Auto-synced companies from parent package:', companiesToSelect.length);
            }
            } else {
              console.log('[Addendum] Parent package has no valid submissions to sync');
            }
          } else {
            console.log('[Addendum] Addendum has not sent messages, skipping sync');
          }
        } catch (error) {
          console.error('Failed to auto-sync companies from parent package:', error);
        }
      };

      syncAddendumCompanies();
    }
  }, [packageData?.isAddendum, packageData?.parentPackageId, parentPackageSubmissions]);

  // Helper function to check if a company has available (non-recipient) users

  const getAvailableUsersForCompany = (company: any) => {

    const companyUsers = company.users || [];

    const existingRecipientEmails = packageSubmissions

      .filter(s => s.recipientCompany === company.name)

      .map(s => s.recipientEmail.toLowerCase());

    

    return companyUsers.filter(user => 

      !existingRecipientEmails.includes(user.email.toLowerCase())

    );

  };



  // Helper function to check if a company is fully submitted (all users are recipients)

  const isCompanyFullySubmitted = (company: any) => {

    const companyUsers = company.users || [];

    if (companyUsers.length === 0) return false; // No users, can't be fully submitted

    

    const availableUsers = getAvailableUsersForCompany(company);

    return availableUsers.length === 0; // Fully submitted if no available users

  };



  // Check if there are new companies selected or new users from existing companies

  const hasNewCompaniesSelected = selectedCompanies.some(companyId => {

    // Find the company by ID and check if its name is not in existing submissions

    // Use the same data source as allCompanies for consistency

    const company = allCompanies.find(c => 

      (c.accId || c.id) === companyId

    );

    

    if (!company) return false;

    

    // If company is completely new, it counts as new

    const isNewCompany = !existingCompanyIds.includes(company.name);

    

    // If company exists but has available users (not fully submitted), it also counts as new

    const hasAvailableUsers = !isCompanyFullySubmitted(company);

    

    const isNew = isNewCompany || hasAvailableUsers;

    

    

    return isNew;

  });



  // Filter companies based on search term and exclude already selected

  const filteredCompanies = allCompanies.filter((company: any) => {

    const companyId = company.accId || company.id;

    // Don't show companies that are already selected

    if (selectedCompanies.includes(companyId)) return false;

    

    if (!companySearchTerm) return true;

    return company.name.toLowerCase().includes(companySearchTerm.toLowerCase()) ||

           (company.trade && company.trade.toLowerCase().includes(companySearchTerm.toLowerCase()));

  });



  // Company Library sync mutation

  const syncMutation = useMutation({

    mutationFn: () => {
      if (!selectedProject?.id) {
        throw new Error('No project selected');
      }
      return apiRequest('/api/company-library/sync', { 
        method: 'POST',
        body: JSON.stringify({ 
          projectId: selectedProject.id,
          hubId: selectedProject.hubId 
        })
      });
    },

    onSuccess: () => {

      toast({ title: "Company library synced successfully" });

      refetchLibrary();

    },

    onError: (error: any) => {

      toast({ 

        title: "Failed to sync company library", 

        description: error.message,

        variant: "destructive" 

      });

    }

  });



  // Handle company selection with user auto-fetch

  const handleCompanySelection = async (company: any) => {

    const companyId = company.accId || company.id;

    const isSelected = selectedCompanies.includes(companyId);

    

    // Prevent selection of fully submitted companies (all users are recipients)

    if (isCompanyFullySubmitted(company)) {

      return;

    }

    

    if (isSelected) {

      // Remove company and its users

      setSelectedCompanies(prev => prev.filter(id => id !== companyId));

      setSelectedRecipients(prev => prev.filter(user => user.companyId !== companyId));

      

      // Refresh company data after removal

      refetchLibrary();

      refetchCompanies();

    } else {

      // Add company and fetch its users

      setSelectedCompanies(prev => [...prev, companyId]);

      

      try {

        let users: any[] = [];

        

        // All companies now have users data from the project-specific API
        users = company.users || [];

        

        // Only select users who are not already recipients

        const availableUsers = getAvailableUsersForCompany({ ...company, users });

        

        const usersWithCompany = availableUsers.map((user: any) => ({

          ...user,

          companyId: companyId,

          companyName: company.name,

          accessCode: company.companyCode, // Use company's companyCode as accessCode

          source: company.source // Pass through the source

        }));

        

        // Deduplicate by email to prevent duplicates - also remove existing users from same company

        setSelectedRecipients(prev => {

          const existingEmails = new Set(prev.map(r => r.email));

          const filteredPrev = prev.filter(r => r.companyId !== companyId); // Remove existing users from this company

          const newUsers = usersWithCompany.filter(user => !existingEmails.has(user.email));

          return [...filteredPrev, ...newUsers];

        });

      } catch (error) {

        console.error('Error fetching company users:', error);

      }

    }

  };



  // Remove individual recipient

  const removeRecipient = async (index: number) => {

    const removedRecipient = selectedRecipients[index];

    if (!removedRecipient) return;




    // Removal is allowed by default even after submission; no settings check
    if (packageData?.submissionStatus !== 'draft') {
      // keep branch for clarity; proceed without blocking
    }



    // If package has been submitted, call API to remove from database (unless it's draft)

    if (packageData?.submissionStatus !== 'draft') {

      try {

        const response = await apiRequest(`/api/tender-packages/${actualPackageId}/members/${encodeURIComponent(removedRecipient.email)}`, {

          method: 'DELETE'

        });

        // Invalidate queries to refresh the UI
        const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
        queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });
        queryClient.invalidateQueries({ queryKey: ['package-submissions', actualPackageId] });

        toast({

          title: "Member Access Removed",

          description: `${removedRecipient.name} access has been removed from the package.`,

        });

      } catch (error) {

        console.error('Error removing member from package:', error);

        
        // Handle 404 errors gracefully - member may have already been removed
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          toast({

            title: "Member Already Removed",

            description: `${removedRecipient.name} was already removed or never added to the package.`,

          });
        } else {
          toast({

            title: "Error",

            description: "Failed to remove member from package.",

            variant: "destructive",

          });
        }

        return;

      }

    }



    setSelectedRecipients(prev => {

      const newRecipients = prev.filter((_, i) => i !== index);

      

      // Check if we need to remove company from selectedCompanies

      if (removedRecipient) {

        const companyId = removedRecipient.companyId;

        if (companyId) {

          // Check if this was the last user from this company

          const remainingUsersFromCompany = newRecipients.filter(r => r.companyId === companyId);

          if (remainingUsersFromCompany.length === 0) {

            // Remove company from selected companies if no users remain

            setSelectedCompanies(prevCompanies => 

              prevCompanies.filter(id => id !== companyId)

            );

          }

        }

      }

      

      return newRecipients;

    });

    

    // Refresh company data to ensure UI is up to date

    refetchLibrary();

    refetchCompanies();

    // Refresh package data to sync with backend changes
    queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

  };



  // Remove entire company

  const removeCompany = async (companyId: string) => {

    // Check if package has been submitted and removal is allowed

    if (packageData?.submissionStatus !== 'draft') {

      // Check tender settings for this project

      try {

        const settingsResponse = await apiRequest(`/api/project-tender-settings/${packageData.projectId}`);

        const allowRemoval = settingsResponse?.allowRemoveCompaniesAfterSubmission || false;

        

        if (!allowRemoval) {

          toast({

            title: "Cannot Remove Company",

            description: "Cannot remove companies after tender submission. Please enable this setting in project tender settings if needed.",

            variant: "destructive",

          });

          return;

        }

      } catch (error) {

        console.error('Error checking tender settings:', error);

        toast({

          title: "Error",

          description: "Failed to check tender settings. Cannot remove company.",

          variant: "destructive",

        });

        return;

      }

    }



    // If package has been submitted, call API to remove from database (unless it's draft)

    if (packageData?.submissionStatus !== 'draft') {

      try {

        // Get company from companyId to get company name
        const company = allCompanies.find((c: any) => (c.accId || c.id) === companyId);
        if (!company) {
          throw new Error('Company not found');
        }

        // Use PATCH method with companyName for consistency
        // This matches the backend endpoint: PATCH /api/tender-packages/:packageId/company/:companyName/access
        await apiRequest(`/api/tender-packages/${actualPackageId}/company/${encodeURIComponent(company.name)}/access`, {

          method: 'PATCH',

          body: JSON.stringify({ accessGranted: 'no' })

        });

        

        toast({

          title: "Company Access Removed",

          description: "Company access has been removed from the package.",

        });

      } catch (error) {

        console.error('Error removing company from package:', error);

        toast({

          title: "Error",

          description: "Failed to remove company from package.",

          variant: "destructive",

        });

        return;

      }

    }



    // Remove company from selected companies

    setSelectedCompanies(prev => prev.filter(id => id !== companyId));

    

    // Remove all users from this company

    setSelectedRecipients(prev => prev.filter(recipient => recipient.companyId !== companyId));

    

    // Refresh company data to ensure UI is up to date

    refetchLibrary();

    refetchCompanies();

    // Refresh package data to sync with backend changes
    queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

  };



  // Sync companies from Autodesk

  const handleSyncCompanies = () => {

    syncMutation.mutate();

  };



  // Create company mutation

  const createCompanyMutation = useMutation({

    mutationFn: async (companyData: any) => {

      return apiRequest('/api/companies', {

        method: 'POST',

        body: JSON.stringify(companyData),

        headers: { 'Content-Type': 'application/json' }

      });

    },

    onSuccess: (data: any) => {

      toast({ title: "Company created successfully" });

      refetchLibrary();

      refetchCompanies();

      

      setShowAddCompanyDialog(false);

      // Clear form

      setCompanyName('');

      setCompanyType('');

      setCompanyEmail('');

      setCompanyPhone('');

      setCompanyAddress('');

      setContactPerson('');


    },

    onError: (error: any) => {

      toast({ 

        title: "Failed to create company", 

        description: error.message,

        variant: "destructive" 

      });

    }

  });





  // Helper function to get file details from the file selector
  const getFileDetailsFromSelector = async (selectedItems: string[], projectId: string) => {
    try {
      const fileDetailsMap: Record<string, any> = {};
      const finalFileIds: string[] = [];
      
      console.log(`[getFileDetailsFromSelector] Processing ${selectedItems.length} selected items`);
      
      // For now, let's use a simpler approach: just pass the selected IDs to the backend
      // and let the backend handle getting the file details
      // This avoids the complexity of trying to match frontend state with backend data
      
      for (const itemId of selectedItems) {
        console.log(`[getFileDetailsFromSelector] Adding item: ${itemId}`);
        finalFileIds.push(itemId);
        
        // Create basic file details - the backend will fetch the real details
        fileDetailsMap[itemId] = {
          name: `File ${itemId.split(':').pop() || itemId}`,
          type: 'file',
          size: 0,
          path: ['Project Files'],
          version: 'V1.0',
          createdBy: '',
          uploader: '',
          versionId: '',
          lastModified: new Date().toISOString()
        };
      }
      
      console.log(`[getFileDetailsFromSelector] Prepared ${finalFileIds.length} files for backend processing`);
      return { fileIds: finalFileIds, fileDetails: fileDetailsMap };
    } catch (error) {
      console.warn('[getFileDetailsFromSelector] Error getting file details:', error);
      return { fileIds: [], fileDetails: {} };
    }
  };


  // Mutations

  const addFilesMutation = useMutation({

    mutationFn: async (selectedItems: string[]) => {
      console.log('Adding files to package:', { packageId: actualPackageId, selectedItems });
      

      // Get project ID from the current package data

      const projectId = packageData?.acc_project_id || packageData?.projectId;
      if (!projectId) {

        throw new Error('No project ID found for this package');

      }

      

      // Get file details from the file selector to pass to backend
      // This will handle both files and folders, expanding folders to their contained files
      const { fileIds, fileDetails } = await getFileDetailsFromSelector(selectedItems, projectId);
      
      if (fileIds.length === 0) {
        throw new Error('No files found to add. Please check your selection.');
      }
      
      console.log(`Adding ${fileIds.length} files to package:`, fileIds);
      
      // Use direct fetch instead of apiRequest to avoid sessionId dependency
      const response = await fetch(`/api/tender-packages/${actualPackageId}/items`, {
        method: 'POST',

        headers: { 'Content-Type': 'application/json' },

        credentials: 'include',
        body: JSON.stringify({ 

          fileIds: fileIds,

          projectId: projectId,
          fileDetails: fileDetails
        }),

      });

      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add files');
      }
      
      const result = await response.json();
      console.log('Add files response:', result);
      return result;
    },

    onSuccess: (data: any) => {

      console.log('Add files response:', data);

      

      // Show success message based on actual results

      const addedCount = data.addedItems || 0;

      const duplicateCount = data.duplicateItems || 0;

      

      if (addedCount > 0) {

        toast({

          title: "Files added successfully",

          description: `${addedCount} file(s) added to package${duplicateCount > 0 ? `, ${duplicateCount} duplicate(s) skipped` : ''}. Checking version status...`,

        });

      } else if (duplicateCount > 0) {

        toast({

          title: "No new files added",

          description: `All ${duplicateCount} selected file(s) already exist in the package`,

        });

      } else {

        toast({

          title: "No files added",

          description: "No files were added to the package. Please check the selection and try again.",

        });

      }

      

      // Refresh package data

      queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

      queryClient.invalidateQueries({ queryKey: ['package-items', actualPackageId] });

      

      // Force refetch package data immediately

      queryClient.refetchQueries({ queryKey: ['package', actualPackageId] });

      

      // Check version status after a shorter delay for faster response

      setTimeout(() => {

        checkVersionStatus();

      }, 500);

      

      setShowAddFilesDialog(false);

      setSelectedFiles([]);

    },

    onError: (error: any) => {

      console.error('Error adding files:', error);

      toast({

        title: "Error adding files",

        description: error.message || "Failed to add files to package. Please try again.",

        variant: "destructive",

      });

    },

  });



  const submitMutation = useMutation({

    mutationFn: async () => {

      // For existing submissions, use existing data; for new submissions, use form data

      const effectiveSubmissionDeadline = hasExistingSubmissions 

        ? (existingSubmissionData?.submissionDeadline instanceof Date 

            ? existingSubmissionData.submissionDeadline.toISOString()

            : existingSubmissionData?.submissionDeadline)

        : submissionDeadline;

      

      const effectiveEmailMessage = hasExistingSubmissions 

        ? existingSubmissionData?.messageToRecipients || emailMessage

        : emailMessage;

        

      const effectiveEmailSubject = hasExistingSubmissions 

        ? existingSubmissionData?.emailSubject || emailSubject

        : emailSubject;

      

      // Submit tender with recipients array for company-based access codes
      // For addendums, use the addendum package ID; for main packages, use actualPackageId
      const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
      const packageNameToUse = packageData?.packageNumber || `Package ${packageIdToUse}`;

      const submissionData = {

        packageId: packageIdToUse,

        packageName: packageNameToUse,

        recipients: selectedRecipients,

        submissionDeadline: effectiveSubmissionDeadline,

        sentBy: user?.email || 'System', // Use actual user email

        messageToRecipients: effectiveEmailMessage,

        emailSubject: effectiveEmailSubject,

      };

      

      return apiRequest('/api/tender-submissions', {

        method: 'POST',

        body: JSON.stringify(submissionData),

      });

    },

    onSuccess: () => {

      const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
      
      // Enhanced data invalidation and refetching
      queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });
      queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });
      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });
      queryClient.invalidateQueries({ queryKey: ['tender-submissions'] });
      
      // Invalidate project-specific queries
      if (packageData?.projectId) {
        queryClient.invalidateQueries({ queryKey: ['tender-packages', packageData.projectId] });
        queryClient.invalidateQueries({ queryKey: ['tender-submissions', packageData.projectId] });
      }

      setShowSubmitDialog(false);

      setSelectedCompanies([]);

      setSelectedRecipients([]);

      // Auto-sync addendum packages for main packages
      if (!packageData?.isAddendum) {
        handleSyncAddendums();
      }

      // Force refetch with a small delay to ensure backend updates are propagated
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['package', actualPackageId] });
        queryClient.refetchQueries({ queryKey: ['tender-packages'] });
        if (packageData?.projectId) {
          queryClient.refetchQueries({ queryKey: ['tender-packages', packageData.projectId] });
        }
      }, 500);

      toast({

        title: "Package submitted successfully",

        description: `Sent to ${selectedRecipients.length} recipients`,

      });

    },

  });



  const createAddendumMutation = useMutation({

    mutationFn: async () => {

      const projectId = (packageData as any)?.projectId || window.location.pathname.match(/\/projects\/([^\/]+)/)?.[1] || '';

      return apiRequest(`/api/tender-packages?projectId=${projectId}`, {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

        },

        body: JSON.stringify({

          name: addendumName,

          description: addendumDescription,

          isAddendum: true,

          parentPackageId: actualPackageId,

          projectId: projectId,

        }),

      });

    },

    onSuccess: (data: any) => {

      // Invalidate all relevant queries to refresh data

      queryClient.invalidateQueries({ queryKey: ['packages'] });

      queryClient.invalidateQueries({ queryKey: ['tender-packages'] });

      queryClient.invalidateQueries({ queryKey: ['package-analytics'] });

      queryClient.invalidateQueries({ queryKey: ['activities'] });

      setShowAddendumDialog(false);

      setAddendumName('');

      setAddendumDescription('');

      toast({

        title: "Addendum created successfully",

        description: `Addendum ${data.packageNumber} created and ready for files`,

      });

      // Use project-specific URL format - get project ID from current URL or package data  

      const currentLocation = window.location.pathname;

      const projectIdMatch = currentLocation.match(/\/projects\/([^\/]+)/);

      const currentProjectId = projectIdMatch ? projectIdMatch[1] : (packageData as any)?.projectId;

      setLocation(`/tender-packages/projects/${currentProjectId}/${data.id}`);

    },

    onError: (error: any) => {

      console.error('Error creating addendum:', error);

      toast({

        title: "Error creating addendum",

        description: error.message || "Failed to create addendum package",

        variant: "destructive",

      });

    },

  });



  // Sync addendum packages mutation

  const syncAddendumsMutation = useMutation({

    mutationFn: async () => {

      return apiRequest(`/api/tender-packages/${actualPackageId}/sync-addendums`, {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

        },

      });

    },

    onSuccess: () => {

      toast({ title: "Addendum packages synced successfully" });

      queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

      queryClient.invalidateQueries({ queryKey: ['packages'] });
      
      // Also refresh all addendum package submissions to show updated data
      queryClient.invalidateQueries({ queryKey: ['package-submissions'] });

    },

    onError: (error: any) => {

      console.error('Error syncing addendum packages:', error);

      toast({ 

        title: "Error syncing addendum packages", 

        description: error?.message || 'Failed to sync addendum packages',

        variant: "destructive"

      });

    },

  });



  const checkUpdatesMutation = useMutation({

    mutationFn: () => apiRequest(`/api/tender-packages/${actualPackageId}/check-updates`, { method: 'POST' }),

    onSuccess: (data: any) => {

      queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

      

      // Show detailed results

      const currentCount = data?.currentCount || 0;

      const outdatedCount = data?.outdatedCount || 0;

      const totalCount = currentCount + outdatedCount;

      

      toast({

        title: "✅ Version check completed",

        description: `${currentCount} files are up to date, ${outdatedCount} files are outdated (Total: ${totalCount} files)`,

        variant: outdatedCount > 0 ? "default" : "default",

      });

    },

    onError: () => {

      toast({

        title: "Version check failed",

        description: "Could not check package versions. Please try again.",

        variant: "destructive",

      });

    },

  });



  const autoUpdateMutation = useMutation({

    mutationFn: () => {

      const projectId = (packageData as any)?.projectId || window.location.pathname.match(/\/projects\/([^\/]+)/)?.[1] || '';

      return apiRequest(`/api/tender-packages/${actualPackageId}/auto-update`, { 

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

        },

        body: JSON.stringify({

          projectId: projectId,

          updatedBy: 'System User'

        }),

      });

    },

    onSuccess: (data: any) => {

      queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

      

      // Show detailed update results

      const updatedCount = data?.updatedCount || 0;

      const totalCount = data?.totalCount || 0;

      

      if (updatedCount > 0) {
        toast({

          title: "✅ Auto-update completed",

          description: `Successfully updated ${updatedCount} files to latest versions (Total: ${totalCount} files)`,

          variant: "default",

        });
      } else {
        toast({

          title: "✅ Auto-update completed",

          description: "All files are already up to date",

          variant: "default",

        });
      }

    },

    onError: (error: any) => {

      toast({

        title: "Auto-update failed",

        description: error.message || "Failed to update files to latest versions. Please try again.",

        variant: "destructive",

      });

    },

  });



  // Share package mutation

  const sharePackageMutation = useMutation({

    mutationFn: async () => {

      const response = await fetch(`/api/tender-packages/${actualPackageId}/share-link`, {

        method: 'POST',

        headers: { 'Content-Type': 'application/json' },

        credentials: 'include',

        body: JSON.stringify({ shareType: 'download', versionType: 'current' })

      });

      

      if (!response.ok) {

        throw new Error('Failed to generate share link');

      }

      

      return await response.json();

    },

    onSuccess: (data) => {

      setShareUrl(data.downloadUrl);

      setShareDialogOpen(true);

    },

  });



  const copyToClipboard = async () => {

    try {

      await navigator.clipboard.writeText(shareUrl);

      toast({

        title: "✅ Link Copied",

        description: "Download link copied to clipboard successfully.",

      });

    } catch (err) {

      toast({

        title: "Copy Failed",

        description: "Failed to copy link to clipboard.",

        variant: "destructive"

      });

    }

  };











  // Export file register state

  const [exportingFileRegister, setExportingFileRegister] = useState(false);

  const [useBackgroundFileRegister, setUseBackgroundFileRegister] = useState(false);

  const [fileRegisterProgress, setFileRegisterProgress] = useState({ current: 0, total: 0 });



  // Download package state

  const [downloadingPackage, setDownloadingPackage] = useState(false);

  const [useBackgroundDownload, setUseBackgroundDownload] = useState(false);

  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  const [downloadStage, setDownloadStage] = useState<'preparing' | 'downloading' | 'zipping' | 'complete'>('preparing');

  

  // Dropdown state

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);



  // Export file register mutation

  const exportFileRegisterMutation = useMutation({

    mutationFn: async () => {

      // 先分析文件数量和大小

      const analysisResponse = await fetch('/api/analyze-download', {

        method: 'POST',

        headers: { 'Content-Type': 'application/json' },

        credentials: 'include',

        body: JSON.stringify({

          projectId: packageData?.acc_project_id,

          itemIds: packageData?.items?.map(item => item.itemId) || [],

        }),

      });



      if (analysisResponse.ok) {

        const analysisData = await analysisResponse.json();

        console.log('📊 File register analysis:', analysisData);

        

        // 检查是否需要后台处理（阈值：10个文件或50MB）

        const shouldUseBackground = analysisData.earlyExit || 

          (analysisData.fileCount > 10) || 

          (analysisData.totalSize > 50 * 1024 * 1024);

        

        if (shouldUseBackground) {

          setUseBackgroundFileRegister(true);

          setExportingFileRegister(true);

          

          // 创建后台任务

          const backgroundResponse = await fetch('/api/reports', {

            method: 'POST',

            headers: { 'Content-Type': 'application/json' },

            credentials: 'include',

            body: JSON.stringify({

              type: 'file_log',

              projectId: packageData?.acc_project_id,

              packageId: actualPackageId,

              packageName: packageData?.name,

              itemIds: packageData?.items?.map(item => item.itemId) || [],

              totalFiles: analysisData.fileCount,

              totalSize: analysisData.totalSize,

            }),

          });



          if (backgroundResponse.ok) {

            const result = await backgroundResponse.json();

            toast({

              title: "📊 File Register Processing",

              description: `Large file register (${analysisData.fileCount} files, ${(analysisData.totalSize / 1024 / 1024).toFixed(1)}MB) is being processed in the background. You'll receive an email when ready.`,

              variant: "default",

              duration: 8000, // 8秒后自动消失（后台处理需要更长时间）

            });

            return { background: true, reportId: result.id };

          }

        }

      }



      // 直接下载（小文件）

      setExportingFileRegister(true);

      setFileRegisterProgress({ current: 0, total: 1 });

      

      const response = await fetch(`/api/tender-packages/${actualPackageId}/export-file-register`, {

        method: 'GET',

        headers: { 'Content-Type': 'application/json' },

        credentials: 'include'

      });

      

      if (!response.ok) {

        throw new Error('Failed to export file register');

      }

      

      setFileRegisterProgress({ current: 1, total: 1 });

      

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');

      a.href = url;

      a.download = `${packageData?.name || 'Package'}_File_Register.xlsx`;

      document.body.appendChild(a);

      a.click();

      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);

      

      return { background: false };

    },

    onSuccess: (result) => {

      setExportingFileRegister(false);

      setFileRegisterProgress({ current: 0, total: 0 });

      setIsDropdownOpen(false); // 处理完成后关闭dropdown

      

      if (result.background) {

        // 后台处理已完成，用户会收到邮件通知

        return;

      }

      

      toast({

        title: "✅ File Register Exported",

        description: "Excel file has been downloaded successfully",

        variant: "default",

        duration: 5000, // 5秒后自动消失

      });

    },

    onError: (error) => {

      console.error('Export error:', error);

      setExportingFileRegister(false);

      setFileRegisterProgress({ current: 0, total: 0 });

      setIsDropdownOpen(false); // 错误时也关闭dropdown

      toast({

        title: "Export Failed",

        description: "Failed to export file register. Please try again.",

        variant: "destructive"

      });

    }

  });



  const downloadPackageMutation = useMutation({

    mutationFn: async () => {

      // 先分析文件数量和大小

      const analysisResponse = await fetch('/api/analyze-download', {

        method: 'POST',

        headers: { 'Content-Type': 'application/json' },

        credentials: 'include',

        body: JSON.stringify({

          projectId: packageData?.acc_project_id,

          itemIds: packageData?.items?.map(item => item.itemId) || [],

        }),

      });



      if (analysisResponse.ok) {

        const analysisData = await analysisResponse.json();

        console.log('📊 Download analysis:', analysisData);

        

        // 检查是否需要后台处理（阈值：10个文件或50MB）

        const shouldUseBackground = analysisData.earlyExit || 

          (analysisData.fileCount > 10) || 

          (analysisData.totalSize > 50 * 1024 * 1024);

        

        if (shouldUseBackground) {

          setUseBackgroundDownload(true);

          setDownloadingPackage(true);

          

          // 创建后台任务

          const backgroundResponse = await fetch('/api/reports', {

            method: 'POST',

            headers: { 'Content-Type': 'application/json' },

            credentials: 'include',

            body: JSON.stringify({

              type: 'download',

              projectId: packageData?.acc_project_id,

              packageId: actualPackageId,

              packageName: packageData?.name,

              itemIds: packageData?.items?.map(item => item.itemId) || [],

              totalFiles: analysisData.fileCount,

              totalSize: analysisData.totalSize,

            }),

          });



          if (backgroundResponse.ok) {

            const result = await backgroundResponse.json();

            toast({

              title: "📦 Package Download Processing",

              description: `Large package (${analysisData.fileCount} files, ${(analysisData.totalSize / 1024 / 1024).toFixed(1)}MB) is being processed in the background. You'll receive an email when ready.`,

              variant: "default",

              duration: 8000, // 8秒后自动消失（后台处理需要更长时间）

            });

            return { background: true, reportId: result.id };

          }

        }

      }



      // 直接下载（小文件）

      setDownloadingPackage(true);

      setDownloadProgress({ current: 0, total: 1 });

      

      const response = await fetch(`/api/tender-packages/${actualPackageId}/download`, {

        method: 'POST',

        credentials: 'include'

      });

      

      if (!response.ok) {

        throw new Error('Download failed');

      }

      

      setDownloadProgress({ current: 1, total: 1 });

      

      return response.blob();

    },

    onSuccess: (result: any) => {

      setDownloadingPackage(false);

      setDownloadProgress({ current: 0, total: 0 });

      setDownloadStage('preparing');

      setIsDropdownOpen(false); // 处理完成后关闭dropdown

      

      if (result.background) {

        // 后台处理已完成，用户会收到邮件通知

        return;

      }

      

      // 直接下载

      const blob = result as Blob;

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');

      a.style.display = 'none';

      a.href = url;

      a.download = `${packageData?.name || 'package'}.zip`;

      document.body.appendChild(a);

      a.click();

      window.URL.revokeObjectURL(url);

      document.body.removeChild(a);

      

      toast({

        title: "Download started",

        description: "Package ZIP file is being downloaded",

        duration: 5000, // 5秒后自动消失

      });

    },

    onError: (error) => {

      console.error('Download error:', error);

      setDownloadingPackage(false);

      setDownloadProgress({ current: 0, total: 0 });

      setDownloadStage('preparing');

      setIsDropdownOpen(false); // 错误时也关闭dropdown

      toast({

        title: "Download Failed",

        description: "Failed to download package. Please try again.",

        variant: "destructive"

      });

    }

  });

  // Company access management mutations
  const updateCompanyAccessMutation = useMutation({
    mutationFn: async ({ packageId, companyName, accessGranted }: { packageId: string; companyName: string; accessGranted: 'yes' | 'no' }) => {
      // Always use PATCH method with companyName for consistency
      // This matches the backend endpoint: PATCH /api/tender-packages/:packageId/company/:companyName/access
      return apiRequest(`/api/tender-packages/${packageId}/company/${encodeURIComponent(companyName)}/access`, {
        method: 'PATCH',
        body: JSON.stringify({ accessGranted })
      });
    },
    onSuccess: (data, variables) => {
      // Update local state
      setCompanyAccessStatus(prev => ({
        ...prev,
        [variables.companyName]: variables.accessGranted
      }));
      
      // Refetch package submissions to get updated data
      queryClient.invalidateQueries({ queryKey: ['package-submissions', actualPackageId] });
      
      toast({
        title: "Access Updated",
        description: `Company access ${variables.accessGranted === 'yes' ? 'granted' : 'removed'} successfully`,
      });
    },
    onError: (error) => {
      console.error('Error updating company access:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update company access. Please try again.",
        variant: "destructive"
      });
    }
  });

  const updateMemberAccessMutation = useMutation({
    mutationFn: async ({ packageId, memberEmail, accessGranted }: { packageId: string; memberEmail: string; accessGranted: 'yes' | 'no' }) => {
      // For removal, use DELETE method
      if (accessGranted === 'no') {
        return apiRequest(`/api/tender-packages/${packageId}/members/${encodeURIComponent(memberEmail)}`, {
          method: 'DELETE'
        });
      } else {
        // For restoration, we might need a different endpoint or method
        // For now, keep the old PATCH method as a fallback
        // TODO: Implement proper restore endpoint if needed
        return apiRequest(`/api/tender-packages/${packageId}/member/${encodeURIComponent(memberEmail)}/access`, {
          method: 'PATCH',
          body: JSON.stringify({ accessGranted })
        });
      }
    },
    onSuccess: (data, variables) => {
      // Refetch package submissions to get updated data
      queryClient.invalidateQueries({ queryKey: ['package-submissions', actualPackageId] });
      
      toast({
        title: "Access Updated",
        description: `Member access ${variables.accessGranted === 'yes' ? 'granted' : 'removed'} successfully`,
      });
    },
    onError: (error) => {
      console.error('Error updating member access:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update member access. Please try again.",
        variant: "destructive"
      });
    }
  });

  // 移除强制保持dropdown打开的逻辑，允许用户自由操作
  // 下载和导出操作将在后台进行，不会阻塞UI



  // Event handlers

  const handleDialogOpenChange = (open: boolean) => {

    setShowAddFilesDialog(open);

    if (!open) {

      setSelectedFiles([]);

      setFolderPath([]);

    }

  };



  // Sync addendum packages handler

  const handleSyncAddendums = async () => {

    try {

      await syncAddendumsMutation.mutateAsync();

    } catch (error) {

      console.error('Failed to sync addendum packages:', error);

    }

  };



  const handleSubmitDialogOpen = async () => {

    setShowSubmitDialog(true);

    setSelectedCompanies([]);

    setSelectedRecipients([]);

    setSubmissionDeadline('');

    // Don't reset emailSubject - let the useEffect handle pre-filling

    setEmailMessage('');

  };



  const handleAddFiles = () => {

    if (selectedFiles.length > 0) {

      addFilesMutation.mutate(selectedFiles);

    }

  };



  const handleSubmit = () => {

    if (selectedRecipients.length > 0) {

      submitMutation.mutate();

    }

  };



  const handleCreateAddendum = () => {

    if (addendumName.trim()) {

      createAddendumMutation.mutate();

    }

  };



  const handleCompanySelect = (companyId: string) => {

    const company = companies?.find(c => c.id && c.id === companyId);

    if (!company) return;



    setSelectedCompanies(prev => {

      const isSelected = prev.includes(companyId);

      if (isSelected) {

        // Remove company and its users

        const companyUsers = company.users || [];

        setSelectedUsers(prevUsers => 

          prevUsers.filter(user => !companyUsers.some(cu => cu.id === user.id))

        );

        return prev.filter(id => id !== companyId);

      } else {

        // Add company and its users

        const companyUsers = company.users || [];

        setSelectedUsers(prevUsers => [

          ...prevUsers,

          ...companyUsers.filter(user => !prevUsers.some(pu => pu.id === user.id))

        ]);

        return [...prev, companyId];

      }

    });

  };



  const handleUserRemove = (userId: string) => {

    setSelectedUsers(prev => prev.filter(user => user.id !== userId));

  };



  const handleAddUser = () => {

    if (newUserEmail && newUserName) {

      const newUser = {

        id: `custom-${Date.now()}`,

        name: newUserName,

        email: newUserEmail,

        firstName: newUserName.split(' ')[0],

        lastName: newUserName.split(' ').slice(1).join(' ') || '',

        jobTitle: 'Custom Contact',

        companyName: 'External'

      };

      setSelectedUsers(prev => [...prev, newUser]);

      setNewUserEmail('');

      setNewUserName('');

    }

  };



  const handleCreateCompany = () => {

    if (!companyName || !companyType) {

      toast({ 

        title: "Please fill in required fields",

        description: "Company name and type are required",

        variant: "destructive" 

      });

      return;

    }










    createCompanyMutation.mutate({

      name: companyName,

      type: companyType,

      email: companyEmail,

      phone: companyPhone,

      address: companyAddress,

      contactPerson: contactPerson,

      users: []

    });

  };















  const handleFileSelect = (fileId: string, checked: boolean) => {

    if (checked) {

      setSelectedFiles(prev => [...prev, fileId]);

    } else {

      setSelectedFiles(prev => prev.filter(id => id !== fileId));

    }

  };



  const handleFolderSelect = (folderId: string, checked: boolean) => {

    if (checked) {

      setSelectedFiles(prev => [...prev, folderId]);

    } else {

      setSelectedFiles(prev => prev.filter(id => id !== folderId));

    }

  };



  const handleFolderClick = (folder: AvailableFile) => {

    setFolderPath(prev => [...prev, folder]);

  };



  const navigateToFolder = (index: number) => {

    if (index === -1) {

      setFolderPath([]);

    } else {

      setFolderPath(prev => prev.slice(0, index + 1));

    }

  };



  if (packageLoading) {

    return (

      <div className="min-h-screen bg-gray-50 flex items-center justify-center">

        <div className="text-center">

          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>

          <p className="text-gray-600">Loading package details...</p>

        </div>

      </div>

    );

  }



  if (packageError || !packageData) {

    return (

      <div className="min-h-screen bg-gray-50 flex items-center justify-center">

        <div className="text-center">

          <p className="text-red-600 mb-4">Failed to load package details</p>

          <Button onClick={() => {

            const currentLocation = window.location.pathname;

            const projectIdMatch = currentLocation.match(/\/projects\/([^\/]+)/);

            const projectId = projectIdMatch ? projectIdMatch[1] : '';

            setLocation(`/tender-packages/projects/${projectId}`);

          }}>

            <ArrowLeft className="w-4 h-4 mr-2" />

            <span className="hidden xl:inline">Back to Packages</span>

          </Button>

        </div>

      </div>

    );

  }



  const isPackageLocked = (packageData as any)?.isLocked;

  // Helper function to check if files can be added to the package
  const canAddFiles = () => {
    if (!packageData) return false;
    
    // Cannot add files if package is locked
    if (isPackageLocked) return false;
    
    // Cannot add files if package is archived
    if (packageData.status === 'archived') return false;
    
    // Only allow adding files when package is in draft or not_sent status
    const allowedStatuses = ['draft', 'not_sent'];
    return allowedStatuses.includes(packageData.submissionStatus);
  };



  return (

    <div className="min-h-screen bg-gray-50">

      {/* 操作状态提示条 */}
      {(downloadingPackage || exportingFileRegister || downloadPackageMutation.isPending || exportFileRegisterMutation.isPending) && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800 font-medium">
                {downloadingPackage || downloadPackageMutation.isPending ? (
                  useBackgroundDownload ? '正在后台处理下载...' : 
                  downloadStage === 'preparing' ? '正在准备下载...' :
                  downloadStage === 'downloading' ? `正在下载... ${downloadProgress.current}/${downloadProgress.total}` :
                  downloadStage === 'zipping' ? '正在创建ZIP文件...' :
                  downloadStage === 'complete' ? '下载完成！' :
                  '正在处理下载...'
                ) : (
                  exportingFileRegister || exportFileRegisterMutation.isPending ? (
                    useBackgroundFileRegister ? '正在后台处理导出...' : 
                    `正在导出文件清单... ${fileRegisterProgress.current}/${fileRegisterProgress.total}`
                  ) : '正在处理...'
                )}
              </span>
            </div>
            <div className="text-xs text-blue-600">
              操作完成后将自动完成
            </div>
          </div>
        </div>
      )}

      {/* Fixed Page Header - responsive layout */}  

      <div className={getPageHeaderClass()}>

        <div className="px-4 py-3 max-w-full mx-auto">

          {/* Mobile layout - single row with more compact spacing */}

          <div className="flex items-center justify-between gap-2">

            {/* Left section: Back button */}

            <div className="shrink-0">

              <Button 

                variant="ghost" 

                onClick={() => {

                  const currentLocation = window.location.pathname;

                  const projectIdMatch = currentLocation.match(/\/projects\/([^\/]+)/);

                  const projectId = projectIdMatch ? projectIdMatch[1] : '';

                  setLocation(`/tender-packages/projects/${projectId}`);

                }}

                className="text-gray-600 hover:text-gray-900 p-2"

                size="sm"

              >

                <ArrowLeft className="w-4 h-4" />

                <span className="hidden xl:inline ml-2">Back to Packages</span>

              </Button>

            </div>



            {/* Center section: Package info with left-aligned layout */}

            <div className="flex items-start gap-2 min-w-0 flex-1 overflow-hidden">

              <Package className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />

              <div className="min-w-0 flex-1">

                <div className="flex items-center gap-3">

                  <h1 className="text-sm md:text-lg font-semibold text-gray-900 truncate">

                    {packageData.packageNumber || `#${packageData.id?.slice(-4)}`} - {packageData.name}

                  </h1>

                  

                  {/* Lock status inline on larger screens */}

                  {isPackageLocked && (

                    <div className="hidden md:flex items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1">

                      <Lock className="w-3 h-3 text-amber-600" />

                      <span className="text-xs font-medium text-amber-800">Read Only</span>

                    </div>

                  )}

                </div>

                

                {/* Lock status and dates for mobile - shown below title, left-aligned */}

                {isPackageLocked && (

                  <div className="flex flex-col md:flex-row md:items-center gap-2 mt-1">

                    <div className="flex md:hidden items-center gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1 w-fit">

                      <Lock className="w-3 h-3 text-amber-600" />

                      <span className="text-xs font-medium text-amber-800">Read Only</span>

                    </div>

                    {packageSubmissions.length > 0 && (

                      <div className="flex items-center gap-3 text-xs text-gray-700 font-medium -ml-7">

                        <span className="flex items-center gap-1">

                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">

                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />

                          </svg>

                          Sent: {(() => {
                            if (!packageSubmissions || packageSubmissions.length === 0) {
                              return 'No message provided';
                            }
                            
                            // Find the latest sent time from all submissions
                            const latestSentAt = packageSubmissions
                              .filter(sub => sub.sentAt && !isNaN(new Date(sub.sentAt).getTime()))
                              .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0]?.sentAt;
                            
                            if (!latestSentAt) {
                              return 'No message provided';
                            }
                            
                            return new Date(latestSentAt).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            });
                          })()}

                        </span>

                        {(() => {
                          if (!packageSubmissions || packageSubmissions.length === 0) {
                            return null;
                          }
                          
                          // Find the latest deadline from all submissions
                          const latestDeadline = packageSubmissions
                            .filter(sub => sub.submissionDeadline && !isNaN(new Date(sub.submissionDeadline).getTime()))
                            .sort((a, b) => new Date(b.submissionDeadline).getTime() - new Date(a.submissionDeadline).getTime())[0]?.submissionDeadline;
                          
                          if (!latestDeadline) {
                            return null;
                          }
                          
                          return (
                            <span className="flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Deadline: {new Date(latestDeadline).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                            </span>
                          );
                        })()}

                      </div>

                    )}

                  </div>

                )}

              </div>

            </div>



            {/* Right section: Status badges - show all badges on all screen sizes */}

            <div className="flex items-center gap-2 shrink-0 flex-wrap">

              <Badge className={getStatusColor(packageData.submissionStatus || 'draft')}>

                {(packageData.submissionStatus === 'draft' || packageData.submissionStatus === 'not_sent') ? 'Draft' : 

                 packageData.submissionStatus === 'sent' ? 'Sent' : 

                 packageData.submissionStatus === 'tender_returned' ? 'Tender Returned' : 

                 packageData.submissionStatus === 'awarded_and_closed' ? 'Awarded and Closed' : 

                 packageData.submissionStatus === 'awarded' ? 'Awarded' :

                 packageData.submissionStatus === 'rejected' ? 'Rejected' :

                 packageData.submissionStatus}

              </Badge>

              <Badge variant="outline" className="inline-flex bg-blue-50 text-blue-700 border-blue-200 text-xs">

                {packageData.totalFiles || 0} Files

              </Badge>

              <Badge variant="outline" className="inline-flex bg-green-50 text-green-700 border-green-200 text-xs">

                {packageData.versionType === 'fixed' ? 'Fixed Version' : 'Current Version'}

              </Badge>

            </div>



            {/* Right section: Action buttons */}

            <div className="flex items-center gap-1 shrink-0">

              {/* Show Submit Tender for unsent packages */}

              {!isPackageLocked && 

                   packageData?.submissionStatus !== 'sent' && 

                   packageData?.submissionStatus !== 'awarded' && 

                   packageData?.submissionStatus !== 'awarded_and_closed' && 

                   packageData?.submissionStatus !== 'tender_returned' ? (

                <Button

                  onClick={(e) => {

                    e.stopPropagation();

                    console.log('Submit Tender button clicked');

                    handleSubmitDialogOpen();

                  }}

                  disabled={!packageData || (packageData.items?.length || 0) === 0}

                  size="sm"

                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs"

                >

                  <Send className="w-3 h-3" />

                  <span className="hidden lg:inline ml-1.5">Submit Tender</span>

                </Button>

              ) : null}

              

              <DropdownMenu 

                open={isDropdownOpen} 

                onOpenChange={(open) => {

                  // 允许用户自由关闭dropdown，即使正在处理下载/导出
                  setIsDropdownOpen(open);

                }}

              >

                <DropdownMenuTrigger asChild>

                  <Button 

                    variant="outline" 

                    size="sm" 

                    className="h-8 px-3 bg-white hover:bg-gray-50 border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"

                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}

                  >

                    <MoreHorizontal className="w-4 h-4" />

                    <span className="hidden lg:inline ml-2 font-medium">Options</span>

                  </Button>

                </DropdownMenuTrigger>

                <DropdownMenuContent 

                  align="end" 

                  className="w-56 p-1 bg-white border border-gray-200 shadow-lg rounded-lg"

                >



                  <DropdownMenuItem 

                    onClick={async () => {

                      if (packageData?.versionType === 'fixed') {

                        // Show tooltip or toast explaining why it's disabled

                        toast({

                          title: "Auto-Update Disabled",

                          description: "Auto-update is not available for Fixed Version packages. Fixed versions preserve the exact file versions when the package was created.",

                          variant: "destructive"

                        });

                        return;

                      }

                      // Directly call auto-update (it will check versions internally)
                      autoUpdateMutation.mutate();

                    }}

                    disabled={autoUpdateMutation.isPending || isPackageLocked || packageData?.versionType === 'fixed'}

                    className={`flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors ${packageData?.versionType === 'fixed' ? 'text-gray-400 cursor-not-allowed' : ''}`}

                  >

                    {autoUpdateMutation.isPending ? (

                      <Loader2 className="w-4 h-4 animate-spin text-orange-600" />

                    ) : (

                      <RefreshCw className={`w-4 h-4 ${packageData?.versionType === 'fixed' ? 'text-gray-400' : 'text-gray-600'}`} />

                    )}

                    <span className="flex-1">

                      {autoUpdateMutation.isPending ? 'Updating...' : 'Auto-Update to Latest'}

                    </span>

                  </DropdownMenuItem>

                  <DropdownMenuItem 

                    onClick={() => {

                      setDownloadingPackage(true); // 立即设置处理状态

                      setDownloadStage('preparing'); // 设置初始阶段

                      // 移除强制保持dropdown打开的逻辑，允许用户自由操作

                      downloadPackageMutation.mutate();

                    }}

                    disabled={downloadingPackage || downloadPackageMutation.isPending}

                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"

                  >

                    {downloadingPackage || downloadPackageMutation.isPending ? (

                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />

                    ) : (

                      <Download className="w-4 h-4 text-gray-600" />

                    )}

                    <span className="flex-1">

                      {downloadingPackage || downloadPackageMutation.isPending ? (

                        useBackgroundDownload ? 'Processing in background...' : 

                        downloadStage === 'preparing' ? 'Preparing...' :

                        downloadStage === 'downloading' ? `Downloading... ${downloadProgress.current}/${downloadProgress.total}` :

                        downloadStage === 'zipping' ? 'Creating ZIP...' :

                        downloadStage === 'complete' ? 'Complete!' :

                        'Processing...'

                      ) : 'Download Package'}

                    </span>

                  </DropdownMenuItem>

                  <DropdownMenuItem 

                    onClick={() => {

                      setExportingFileRegister(true); // 立即设置处理状态

                      // 移除强制保持dropdown打开的逻辑，允许用户自由操作

                      exportFileRegisterMutation.mutate();

                    }}

                    disabled={exportingFileRegister || exportFileRegisterMutation.isPending || !packageData?.items?.length}

                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"

                  >

                    {exportingFileRegister || exportFileRegisterMutation.isPending ? (

                      <Loader2 className="w-4 h-4 animate-spin text-green-600" />

                    ) : (

                      <FileSpreadsheet className="w-4 h-4 text-gray-600" />

                    )}

                    <span className="flex-1">

                      {exportingFileRegister 

                        ? (useBackgroundFileRegister ? 'Processing in background...' : `Exporting... ${fileRegisterProgress.current}/${fileRegisterProgress.total}`)

                        : exportFileRegisterMutation.isPending 

                          ? 'Exporting...' 

                          : 'Export File Register'

                      }

                    </span>

                  </DropdownMenuItem>

                  <DropdownMenuItem 

                    onClick={() => sharePackageMutation.mutate()}

                    disabled={sharePackageMutation.isPending}

                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"

                  >

                    {sharePackageMutation.isPending ? (

                      <Loader2 className="w-4 h-4 animate-spin text-purple-600" />

                    ) : (

                      <Share2 className="w-4 h-4 text-gray-600" />

                    )}

                    <span className="flex-1">

                      {sharePackageMutation.isPending ? 'Generating Link...' : 'Share Download Link'}

                    </span>

                  </DropdownMenuItem>



                </DropdownMenuContent>

              </DropdownMenu>

            </div>

          </div>

          

        </div>

      </div>



      {/* Main Content with top padding to account for both headers (64px global + 64px page header) */}

      <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 max-w-full mx-auto">

        <div className="flex flex-col space-y-2 h-full">



        {/* Add minimal spacing before Package Successfully Submitted Banner */}

        <div className="mb-0"></div>

        

        {/* Package Status Banner - moved above tabs */}

        {/* Show banner for all packages */}

        {packageData && (

          <Card className={`shadow-sm mb-0 ${

            packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 

              ? 'border-green-200 bg-green-50' 

              : 'border-gray-200 bg-gray-50'

          }`}>

            <CardContent className="py-2 h-20 flex items-center">

                {/* Single row: Package Status, References, and Create Addendum */}

                <div className="flex items-center justify-between w-full">

                {/* Left side: Package Status */}

                <div className="flex items-center space-x-3">

                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${

                    packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 

                      ? 'bg-green-500' 

                      : 'bg-gray-500'

                  }`}>

                    {packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 ? (

                      <CheckCircle className="w-4 h-4 text-white" />

                    ) : (

                      <Clock className="w-4 h-4 text-white" />

                    )}

                  </div>

                  <div>

                    <h3 className={`text-lg font-semibold mb-1 ${

                      packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 

                        ? 'text-green-800' 

                        : 'text-gray-800'

                    }`}>

                      {packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 

                        ? 'Package Successfully Submitted'

                        : 'Package Not Submitted Yet'

                      }

                    </h3>

                    <p className={`text-sm ${

                      packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 

                        ? 'text-green-700' 

                        : 'text-gray-600'

                    }`}>

                      {(() => {
                        const validSubmissions = getValidSubmissions(packageSubmissions, packageData?.isAddendum);
                        return packageData.submissionStatus === 'sent' || validSubmissions.length > 0 
                          ? `Sent to ${validSubmissions.length} recipient${validSubmissions.length === 1 ? '' : 's'} from ${new Set(validSubmissions.map(s => s.recipientCompany)).size} ${new Set(validSubmissions.map(s => s.recipientCompany)).size === 1 ? 'company' : 'companies'} • Monitor activity below`
                          : 'Package is ready for submission • Add documents and submit when ready';
                      })()}

                    </p>

                  </div>

                </div>

                

                {/* Center: Enhanced References */}

                <div className="flex flex-col space-y-1 max-h-16 overflow-hidden">

                  <div className="flex items-center space-x-2">

                    <span className={`text-sm font-medium ${
                      packageData.submissionStatus === 'sent' || packageSubmissions.length > 0 
                        ? 'text-green-800' 
                        : 'text-gray-800'
                    }`}>References:</span>

                    {(() => {
                      if (packageData?.isAddendum) {
                        // For addendum, count main package + other addendums
                        const mainPackage = packageData.mainPackage ? 1 : 0;
                        const otherAddendums = packageData.addendums?.length || 0;
                        const totalCount = mainPackage + otherAddendums;
                        const hasMore = otherAddendums > 1;
                        return (
                          <span className="text-sm text-gray-600">
                            ({totalCount}){hasMore ? (
                              <button
                                onClick={() => setShowAllReferencesDialog(true)}
                                className="ml-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              >
                                - Show All
                              </button>
                            ) : ''}
                          </span>
                        );
                      } else {
                        // For main package, count addendums
                        const addendumCount = packageData?.addendums?.length || 0;
                        const hasMore = addendumCount > 2;
                        return (
                          <span className="text-sm text-gray-600">
                            ({addendumCount}){hasMore ? (
                              <button
                                onClick={() => setShowAllReferencesDialog(true)}
                                className="ml-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              >
                                - Show All
                              </button>
                            ) : ''}
                          </span>
                        );
                      }
                    })()}

                  </div>

                  {packageData?.isAddendum ? (

                    // For addendum packages, show main package + other addendums (max 2 lines)
                    <div className="space-y-1">
                      {/* Main Package */}
                      {packageData.mainPackage && (
                        <div className="text-sm text-gray-600">
                          <button
                            onClick={() => setLocation(`/tender-packages/projects/${packageData.acc_project_id}/${packageData.mainPackage.id}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          >
                            {packageData.mainPackage.packageNumber} - {packageData.mainPackage.name || 'Main Package'} [MAIN PACK]
                          </button>
                        </div>
                      )}

                      {/* Other Addendums - show first one */}
                      {packageData.addendums && packageData.addendums.length > 0 ? (
                        <div className="text-sm text-gray-600">
                          <button
                            onClick={() => setLocation(`/tender-packages/projects/${packageData.acc_project_id}/${packageData.addendums[0].id}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                          >
                            {packageData.addendums[0].packageNumber} - {packageData.addendums[0].name || 'Addendum'}
                          </button>
                          {/* Show ellipsis on the right if there are more addendums */}
                          {packageData.addendums.length > 1 && (
                            <span className="ml-2 text-sm text-gray-500">
                              +{packageData.addendums.length - 1} more...
                            </span>
                          )}
                        </div>
                      ) : (
                        // If no other addendums, show a placeholder or empty line
                        <div className="text-sm text-gray-400">
                          No other addendums
                        </div>
                      )}

                      {/* Debug info - remove in production */}
                    </div>

                  ) : (

                    // For main packages, show addendums info (max 2 lines)
                    packageData?.addendums && packageData.addendums.length > 0 ? (

                      <div className="space-y-1">

                        {packageData.addendums.slice(0, 2).map((addendum, index) => (

                          <div key={addendum.id} className="text-sm text-gray-600">

                            <button
                              onClick={() => setLocation(`/tender-packages/projects/${packageData.acc_project_id}/${addendum.id}`)}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >

                              {addendum.packageNumber || `WP${addendum.id?.slice(-4)}`} - {addendum.name || 'Addendum'}

                            </button>

                            {/* Show ellipsis on the right of the second reference */}
                            {index === 1 && packageData.addendums.length > 2 && (
                              <span className="ml-2 text-sm text-gray-500">
                                +{packageData.addendums.length - 2} more...
                              </span>
                            )}

                          </div>

                        ))}


                      </div>

                    ) : (

                      <div className="text-sm text-gray-500">--</div>

                    )

                  )}

                </div>

                

                {/* Right side: Create Addendum button - only for main packages */}

                <div>

                  {!packageData?.isAddendum ? (

                    <Button

                      variant="outline"

                      size="sm"

                      className="border-green-300 text-green-700 hover:bg-green-100"

                      onClick={() => {

                        setShowAddendumDialog(true);

                      }}

                    >

                      <Package className="w-4 h-4 mr-2" />

                      Create Addendum

                    </Button>

                  ) : (

                    <Button

                      variant="outline"

                      size="sm"

                      className="border-orange-400 text-orange-800 bg-orange-100 cursor-not-allowed font-semibold"

                      disabled

                    >

                      <Package className="w-4 h-4 mr-2" />

                      Is Addendum

                    </Button>

                  )}

                </div>

              </div>

            </CardContent>

          </Card>

        )}

        {/* Main Content with Tabs */}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

          <div className="relative mb-2">

            <TabsList className="w-full bg-white border border-gray-200 p-0 h-auto rounded-lg shadow-sm">

              <div className="grid w-full grid-cols-4">

                <TabsTrigger 

                  value="overview" 

                  className="relative data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:border-blue-600 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 rounded-l-lg font-medium py-3 border-r border-gray-200"

                  onClick={() => {

                    // Refresh overview data when tab is clicked using correct query keys

                    queryClient.invalidateQueries({ queryKey: ['package', actualPackageId, isAddendumView ? addendumId : null] });

                    const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
                    queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });

                  }}

                >

                  <FileText className="w-4 h-4 mr-2" />

                  Overview

                </TabsTrigger>

                <TabsTrigger 

                  value="submissions" 

                  className="relative data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:border-blue-600 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 font-medium py-3 border-r border-gray-200"

                  onClick={() => {

                    // Refresh submission data when tab is clicked using correct query key

                    const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
                    queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });

                    queryClient.invalidateQueries({ queryKey: ['package', actualPackageId, isAddendumView ? addendumId : null] });

                  }}

                >

                  <Send className="w-4 h-4 mr-2" />

                  Invitation to Tender

                  {(() => {
                    const validSubmissions = getValidSubmissions(packageSubmissions, packageData?.isAddendum);
                    return validSubmissions.length > 0 && (
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                        {new Set(validSubmissions.map(s => s.recipientCompany)).size}
                      </span>
                    );
                  })()}

                </TabsTrigger>

                <TabsTrigger 

                  value="returns" 

                  className="relative data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:border-blue-600 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 font-medium py-3 border-r border-gray-200"

                  onClick={() => {

                    // Refresh tender returns data when tab is clicked using correct query key

                    // Always use actualPackageId for API calls

                    queryClient.invalidateQueries({ queryKey: ['package-returns', actualPackageId] });

                    const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
                    queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });

                  }}

                >

                  <Upload className="w-4 h-4 mr-2" />

                  Tender Returns

                  {returnsCount > 0 && (

                    <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">

                      {returnsCount}

                    </span>

                  )}

                </TabsTrigger>

                <TabsTrigger 

                  value="analytics" 

                  className="relative data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:border-blue-600 text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-all duration-200 rounded-r-lg font-medium py-3"

                  onClick={() => {

                    // Refresh analytics data when tab is clicked using correct query keys

                    const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
                    queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });

                    queryClient.invalidateQueries({ queryKey: ['package', actualPackageId, isAddendumView ? addendumId : null] });

                    queryClient.invalidateQueries({ queryKey: ['package-analytics'] });

                  }}

                >

                  <BarChart3 className="w-4 h-4 mr-2" />

                  Analytics

                </TabsTrigger>

              </div>

            </TabsList>

          </div>

          



          {/* Overview Tab */}

          <TabsContent value="overview" className="space-y-6">





            {/* Tender Package Contents */}

            <Card className="border-gray-200 shadow-sm">

              <CardHeader className="pb-3">

                <div className="flex items-center justify-between">

                  <CardTitle className="flex items-center text-lg font-semibold text-gray-900">

                    <Package className="w-5 h-5 mr-2 text-blue-600" />

                    Tender Package ({packageData?.totalFiles || 0} files)

                  </CardTitle>

                  <div className="flex items-center space-x-2">

                    <Button

                      variant="ghost"

                      size="sm"

                      onClick={() => setIsPackageContentsExpanded(!isPackageContentsExpanded)}

                      className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"

                    >

                      {isPackageContentsExpanded ? (

                        <ChevronUp className="w-4 h-4 mr-1" />

                      ) : (

                        <ChevronDown className="w-4 h-4 mr-1" />

                      )}

                      {isPackageContentsExpanded ? 'Hide' : 'Show'}

                    </Button>

                    {canAddFiles() && (
                      // Only show "Add Files" button when package is in draft/not_sent status and not locked
                      // "Create Addendum" is already shown at the top of the page
                      <Button
                        onClick={() => setShowAddFilesDialog(true)}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Files
                      </Button>
                    )}

                  </div>

                </div>

              </CardHeader>

              {isPackageContentsExpanded && (

                <CardContent>

                  {packageData?.items && packageData.items.length > 0 ? (

                  <div className="space-y-4">

                    {/* Bulk Actions Bar */}

                    {selectedPackageItems.length > 0 && !isPackageLocked && (

                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">

                        <div className="flex items-center space-x-3">

                          <span className="text-sm font-medium text-blue-900">

                            {selectedPackageItems.length} items selected

                          </span>

                        </div>

                        <div className="flex items-center space-x-2">

                          <Button

                            size="sm"

                            variant="outline"

                            className="border-blue-300 text-blue-700 hover:bg-blue-100"

                            disabled={isDownloadingSelectedFiles}

                            onClick={async () => {

                              // Download selected files

                              setIsDownloadingSelectedFiles(true);

                              try {

                                const response = await fetch('/api/package-items/download-selected', {

                                  method: 'POST',

                                  credentials: 'include',

                                  headers: {

                                    'Content-Type': 'application/json',

                                  },

                                  body: JSON.stringify({

                                    packageId: actualPackageId,

                                    itemIds: selectedPackageItems

                                  }),

                                });

                                

                                if (response.ok) {

                                  // Wait for the complete blob to be downloaded
                                  // This ensures the loading state stays active until the entire ZIP is ready
                                  const blob = await response.blob();

                                  // Only end loading state after blob is fully downloaded
                                  setIsDownloadingSelectedFiles(false);

                                  const url = window.URL.createObjectURL(blob);

                                  const a = document.createElement('a');

                                  a.style.display = 'none';

                                  a.href = url;

                                  a.download = `${packageData?.name || 'package'}_selected_files.zip`;

                                  document.body.appendChild(a);

                                  a.click();

                                  window.URL.revokeObjectURL(url);

                                  document.body.removeChild(a);

                                  

                                  toast({

                                    title: "Download completed",

                                    description: `Successfully downloaded ${selectedPackageItems.length} selected files`,

                                  });

                                } else {

                                  setIsDownloadingSelectedFiles(false);

                                  const errorData = await response.json().catch(() => ({}));

                                  throw new Error(errorData.error || 'Download failed');

                                }

                              } catch (error: any) {

                                setIsDownloadingSelectedFiles(false);

                                toast({

                                  title: "Download failed",

                                  description: error.message || "Please try again later",

                                  variant: "destructive",

                                });

                              }

                            }}

                          >

                            {isDownloadingSelectedFiles ? (

                              <>

                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />

                                Downloading...

                              </>

                            ) : (

                              <>

                                <Download className="w-4 h-4 mr-1" />

                                Download

                              </>

                            )}

                          </Button>

                          <Button

                            size="sm"

                            variant="outline"

                            className="border-red-300 text-red-700 hover:bg-red-100"

                            onClick={async () => {

                              // Remove selected files using batch delete API

                              try {

                                const response = await fetch(`/api/tender-packages/${actualPackageId}/items`, {

                                  method: 'DELETE',

                                  headers: {

                                    'Content-Type': 'application/json',

                                  },

                                  body: JSON.stringify({

                                    itemIds: selectedPackageItems

                                  }),

                                });

                                

                                if (response.ok) {

                                  const result = await response.json();

                                  setSelectedPackageItems([]);

                                  queryClient.invalidateQueries({ queryKey: ['package', actualPackageId] });

                                  toast({

                                    title: "Files removed",

                                    description: `${result.deletedCount} files removed from package`,

                                  });

                                } else {

                                  throw new Error('Batch delete failed');

                                }

                              } catch (error) {

                                toast({

                                  title: "Remove failed",

                                  description: "Please try again later",

                                  variant: "destructive",

                                });

                              }

                            }}

                          >

                            <Trash2 className="w-4 h-4 mr-1" />

                            Remove

                          </Button>

                        </div>

                      </div>

                    )}

                    

                    <FolderTreeView 

                      items={packageData.items.map(convertPackageItemToFileItem)} 

                      isLocked={isPackageLocked}

                      selectedItems={selectedPackageItems}

                      onSelectItem={(itemId, checked) => {

                        if (checked) {

                          setSelectedPackageItems(prev => [...prev, itemId]);

                        } else {

                          setSelectedPackageItems(prev => prev.filter(id => id !== itemId));

                        }

                      }}

                      onSelectAll={(checked) => {

                        if (checked) {

                          setSelectedPackageItems(packageData.items.map(item => item.id));

                        } else {

                          setSelectedPackageItems([]);

                        }

                      }}

                      onDataRefresh={() => {

                        // Refresh package data to get updated version labels

                        queryClient.invalidateQueries({ queryKey: ['package', actualPackageId, isAddendumView ? addendumId : null] });

                      }}

                    />

                  </div>

                ) : (

                  <div className="text-center py-12">

                    <FolderOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />

                    <p className="text-gray-500">No files in this package yet.</p>

                    {canAddFiles() && (

                      <Button

                        onClick={() => setShowAddFilesDialog(true)}

                        className="mt-4"

                        variant="outline"

                      >

                        <Plus className="w-4 h-4 mr-2" />

                        Add Files

                      </Button>

                    )}

                    </div>

                  )}

                </CardContent>

              )}

            </Card>



            {/* Package Submission Status */}

            <div className="space-y-6">

              {/* Addendum Status - Show when main package has addendums */}

              {packageData && !packageData.isAddendum && (

                <AddendumStatusCard packageId={actualPackageId} />

              )}

            </div>



            {/* Package Activity - Full width card */}

            <PackageActivityCard packageId={actualPackageId!} />

          </TabsContent>



          {/* Submissions Tab */}

          <TabsContent value="submissions" className="space-y-6">

            {packageData?.isAddendum ? (

              // For addendum packages, show submission interface with main package style

              <div className="space-y-6">

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">

                  <div className="flex items-center">

                    <Package className="w-5 h-5 text-blue-600 mr-2" />

                    <h3 className="text-sm font-medium text-blue-900">Addendum Package</h3>

                  </div>

                  <p className="text-sm text-blue-700 mt-1">

                    This is an addendum package. You can send it to the same recipients as the parent package.

                  </p>

                </div>

                

                {/* Show existing submissions if any - using main package style */}

                {hasExistingSubmissions ? (

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">

                    {/* Header with title, dates, and email info aligned */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-600 rounded-lg">
                          <Send className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">Submission Summary</h3>
                          <p className="text-sm text-gray-600">Package delivered to {[...new Set(getValidSubmissions(packageSubmissions, packageData?.isAddendum).map(s => s.recipientCompany || (s as any).recipientCompany))].length} companies</p>
                          {/* Dates aligned with title */}
                          <div className="flex items-center space-x-4 text-sm text-blue-800 font-medium mt-2">
                            <span>📅 Sent: {(() => {
                              if (!packageSubmissions || packageSubmissions.length === 0) {
                                return 'No message provided';
                              }
                              
                              // Find the latest sent time from all submissions
                              const latestSentAt = packageSubmissions
                                .filter(sub => sub.sentAt && !isNaN(new Date(sub.sentAt).getTime()))
                                .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0]?.sentAt;
                              
                              if (!latestSentAt) {
                                return 'No message provided';
                              }
                              
                              return new Date(latestSentAt).toLocaleDateString();
                            })()}</span>
                            <span>⏰ Deadline: {(() => {
                              if (!packageSubmissions || packageSubmissions.length === 0) {
                                return 'Not set';
                              }
                              
                              // Find the latest deadline from all submissions
                              const latestDeadline = packageSubmissions
                                .filter(sub => sub.submissionDeadline && !isNaN(new Date(sub.submissionDeadline).getTime()))
                                .sort((a, b) => new Date(b.submissionDeadline).getTime() - new Date(a.submissionDeadline).getTime())[0]?.submissionDeadline;
                              
                              if (!latestDeadline) {
                                return 'Not set';
                              }
                              
                              return new Date(latestDeadline).toLocaleDateString();
                            })()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Email Subject and Message aligned with title */}
                      <div className="flex-1 max-w-md space-y-3 ml-8">
                        {/* Email Subject with expandable Message to Recipients */}
                        {(() => {
                          if (!packageSubmissions || packageSubmissions.length === 0) {
                            return null;
                          }
                          
                          // Find the submission with the most complete data (email subject and message)
                          const bestSubmission = packageSubmissions
                            .filter(sub => sub.emailSubject && sub.emailSubject !== 'null' && sub.emailSubject.trim() !== '')
                            .sort((a, b) => {
                              // Prioritize submissions with sentAt (actually sent)
                              if (a.sentAt && !b.sentAt) return -1;
                              if (!a.sentAt && b.sentAt) return 1;
                              return 0;
                            })[0];
                          
                          if (!bestSubmission?.emailSubject) {
                            return null;
                          }
                          
                          return (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-start space-x-2">
                                <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-blue-700 mb-1">Email Subject:</div>
                                  <div className="text-sm text-blue-600 mb-3">{bestSubmission.emailSubject}</div>
                                  
                                  {/* Message to Recipients - Expandable Button */}
                                  {bestSubmission?.messageToRecipients && 
                                   bestSubmission.messageToRecipients.trim() && 
                                   bestSubmission.messageToRecipients !== 'No message provided' && (
                                    <div>
                                      <button
                                        className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                        onClick={(e) => {
                                          const btn = e.currentTarget;
                                          const content = btn.nextElementSibling as HTMLElement;
                                          const icon = btn.querySelector('.expand-icon') as HTMLElement;
                                          
                                          if (content.style.display === 'none' || content.style.display === '') {
                                            content.style.display = 'block';
                                            icon.style.transform = 'rotate(180deg)';
                                          } else {
                                            content.style.display = 'none';
                                            icon.style.transform = 'rotate(0deg)';
                                          }
                                        }}
                                      >
                                        <span>Message to Recipients</span>
                                        <svg 
                                          className="expand-icon w-4 h-4 transition-transform duration-200" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      
                                      <div 
                                        className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-600 whitespace-pre-wrap"
                                        style={{ display: 'none' }}
                                      >
                                        {bestSubmission.messageToRecipients}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>


                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Companies</p>
                            <p className="text-2xl font-bold text-gray-900">{[...new Set(getValidSubmissions(packageSubmissions, packageData?.isAddendum).map(s => s.recipientCompany))].length}</p>
                          </div>
                          <Building className="w-8 h-8 text-blue-600" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Total Views</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {packageSubmissions.reduce((sum, s) => sum + (s.viewCount || 0), 0)}
                            </p>
                          </div>
                          <Eye className="w-8 h-8 text-green-600" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Downloads</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {packageSubmissions.reduce((sum, s) => sum + (s.downloadCount || 0), 0)}
                            </p>
                          </div>
                          <Download className="w-8 h-8 text-purple-600" />
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Viewed By</p>
                            <p className="text-2xl font-bold text-gray-900">
                              {packageSubmissions.filter(s => s.firstViewedAt).length}
                            </p>
                          </div>
                          <CheckCircle className="w-8 h-8 text-orange-600" />
                        </div>
                      </div>
                    </div>

                    {/* Company Details */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold text-gray-900">Company Details & Access</h4>
                        <div className="flex gap-2">
                          {!packageData?.isAddendum && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowSubmitDialog(true)}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add More Companies
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {/* Group submissions by company and show one card per company */}
                        {Array.isArray(packageSubmissions) && Object.entries(
                          getValidSubmissions(packageSubmissions, packageData?.isAddendum).reduce((groups, submission) => {
                            const companyName = submission.recipientCompany;
                            if (!groups[companyName]) {
                              groups[companyName] = [];
                            }
                            groups[companyName].push(submission);
                            return groups;
                          }, {} as Record<string, typeof packageSubmissions>)
                        ).map(([companyName, companySubmissions]) => {
                          // Aggregate data for the company
                          const totalViews = Array.isArray(companySubmissions) ? companySubmissions.reduce((sum, s) => sum + (s.viewCount || 0), 0) : 0;
                          const totalDownloads = Array.isArray(companySubmissions) ? companySubmissions.reduce((sum, s) => sum + (s.downloadCount || 0), 0) : 0;
                          const hasViewed = Array.isArray(companySubmissions) ? companySubmissions.some(s => s.firstViewedAt) : false;
                          const lastViewed = Array.isArray(companySubmissions) ? companySubmissions.reduce((latest, s) => {
                            if (!s.lastViewedAt) return latest;
                            if (!latest) return s.lastViewedAt;
                            return new Date(s.lastViewedAt) > new Date(latest) ? s.lastViewedAt : latest;
                          }, null as string | null) : null;
                          const accessCode = Array.isArray(companySubmissions) ? companySubmissions[0]?.companyAccessCode : '';
                          const userEmails = Array.isArray(companySubmissions) ? companySubmissions.map(s => s.recipientEmail).join(', ') : '';
                          
                          // Check if company has access (all members must have access_granted = 'yes')
                          const hasAccess = Array.isArray(companySubmissions) ? 
                            companySubmissions.every(s => s.accessGranted === 'yes' || s.accessGranted === undefined) : true;
                          
                          // Check if any member has access (for company status)
                          const anyMemberHasAccess = Array.isArray(companySubmissions) ? 
                            companySubmissions.some(s => s.accessGranted === 'yes' || s.accessGranted === undefined) : true;

                          // Check if this is an addendum package
                          const isAddendumPackage = !!packageData?.isAddendum;

                          return (
                            <div key={companyName} className={`border rounded-lg p-5 hover:shadow-md transition-all ${
                              anyMemberHasAccess 
                                ? 'bg-white border-gray-200' 
                                : 'bg-gray-100 border-gray-300 opacity-60'
                            }`}>
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1 min-w-0">
                                  <div className={`font-semibold truncate text-lg ${
                                    anyMemberHasAccess ? 'text-gray-900' : 'text-gray-500'
                                  }`}>{companyName}</div>
                                  <div className={`text-sm truncate ${
                                    anyMemberHasAccess ? 'text-gray-600' : 'text-gray-400'
                                  }`}>{userEmails}</div>
                                  <div className={`text-xs mt-1 ${
                                    anyMemberHasAccess ? 'text-gray-500' : 'text-gray-400'
                                  }`}>{Array.isArray(companySubmissions) ? companySubmissions.length : 0} recipients</div>
                                </div>
                                <div className="ml-3 flex items-center gap-3">
                                  {anyMemberHasAccess ? (
                                    // 如果有任何成员仍有访问权限，显示 'X' 按钮以移除访问
                                    // 对于子包，禁用删除按钮（数据从主包同步）
                                    <button
                                      onClick={() => {
                                        // Don't allow company removal in addendum packages (synced from parent)
                                        if (isAddendumPackage) {
                                          toast({
                                            title: "Cannot remove company",
                                            description: "Cannot remove companies in addendum packages. These are synced from the main package.",
                                            variant: "destructive"
                                          });
                                          return;
                                        }
                                        setCompanyToRemove(companyName);
                                        setShowRemoveCompanyDialog(true);
                                      }}
                                      disabled={isAddendumPackage}
                                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 border-2 ${
                                        isAddendumPackage 
                                          ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50' 
                                          : 'bg-red-100 border-red-200 hover:bg-red-200 hover:border-red-300'
                                      }`}
                                      title={isAddendumPackage ? 'Cannot remove in addendum packages' : 'Remove company access'}
                                    >
                                      <X className={`w-4 h-4 font-bold ${
                                        isAddendumPackage ? 'text-gray-500' : 'text-red-600'
                                      }`} />
                                    </button>
                                  ) : (
                                    // 如果所有成员的访问权限都已移除，显示 '撤回' 按钮
                                    // 对于子包，禁用撤回按钮（数据从主包同步）
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        // Don't allow company restore in addendum packages (synced from parent)
                                        if (isAddendumPackage) {
                                          toast({
                                            title: "Cannot revert",
                                            description: "Cannot revert company access changes in addendum packages. These are synced from the main package.",
                                            variant: "destructive"
                                          });
                                          return;
                                        }
                                        updateCompanyAccessMutation.mutate({
                                          packageId: actualPackageId,
                                          companyName,
                                          accessGranted: 'yes'
                                        });
                                      }}
                                      className={`px-2 py-1 h-8 w-8 flex-shrink-0 border-2 ${
                                        isAddendumPackage 
                                          ? 'border-gray-300 opacity-50' 
                                          : 'border-gray-300 hover:border-gray-400'
                                      }`}
                                      title={isAddendumPackage ? 'Cannot revert in addendum packages' : 'Restore company access'}
                                      disabled={isAddendumPackage || updateCompanyAccessMutation.isPending}
                                    >
                                      {updateCompanyAccessMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RotateCcw className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </div>
                              
                              <div className="mb-4">
                                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Access Code</div>
                                <div className="font-mono text-lg font-bold text-gray-900 bg-gray-50 px-3 py-2 rounded border">
                                  {accessCode || 'N/A'}
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3 mb-4">
                                <div className="text-center">
                                  <div className="text-lg font-bold text-blue-600">{totalViews}</div>
                                  <div className="text-xs text-gray-500">Views</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-lg font-bold text-purple-600">{totalDownloads}</div>
                                  <div className="text-xs text-gray-500">Downloads</div>
                                </div>
                              </div>
                              
                              {lastViewed && (
                                <div className="text-xs text-gray-500 mb-3">
                                  Last viewed: {new Date(lastViewed).toLocaleDateString()}
                                </div>
                              )}
                              
                              <div className="space-y-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-xs"
                                  onClick={() => {
                                    // Set selected company for recipient management
                                    setSelectedCompanyForManagement(companyName);
                                    setShowCompanyRecipientsDialog(true);
                                  }}
                                >
                                  <User className="w-3 h-3 mr-1" />
                                  View/Manage ({Array.isArray(companySubmissions) ? companySubmissions.length : 0} recipients)
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-xs"
                                  onClick={() => {
                                    if (accessCode) {
                                      navigator.clipboard.writeText(accessCode);
                                      toast({
                                        title: "Copied",
                                        description: "Access code copied to clipboard",
                                      });
                                    }
                                  }}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  Copy Access Code
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full text-xs"
                                  onClick={() => {
                                    // TODO: Implement activity timeline view
                                    console.log('View activity timeline for', companyName);
                                  }}
                                >
                                  <Activity className="w-3 h-3 mr-1" />
                                  View Activity Timeline
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                ) : (

                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">

                    <Send className="w-12 h-12 mx-auto text-gray-400 mb-4" />

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Submissions Yet</h3>

                    <p className="text-gray-500 mb-4">This addendum package hasn't been submitted to any companies.</p>

                    {!isPackageLocked && packageData?.items?.length > 0 && (

                      <Button

                        onClick={handleSubmitDialogOpen}

                        className="bg-green-600 hover:bg-green-700 text-white"

                      >

                        <Send className="w-4 h-4 mr-2" />

                        Add Companies to Parent Package

                      </Button>

                    )}

                  </div>

                )}

              </div>

            ) : packageSubmissions.length > 0 ? (

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-sm">

                {/* Header with title, dates, and email info aligned */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-600 rounded-lg">
                      <Send className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Submission Summary</h3>
                      <p className="text-sm text-gray-600">Package delivered to {[...new Set(getValidSubmissions(packageSubmissions, packageData?.isAddendum).map(s => s.recipientCompany || (s as any).recipientCompany))].length} companies</p>
                      {/* Dates aligned with title */}
                      <div className="flex items-center space-x-4 text-sm text-blue-800 font-medium mt-2">
                        <span>📅 Sent: {(() => {
                          if (!packageSubmissions || packageSubmissions.length === 0) {
                            return 'No message provided';
                          }
                          
                          // Find the latest sent time from all submissions
                          const latestSentAt = packageSubmissions
                            .filter(sub => sub.sentAt && !isNaN(new Date(sub.sentAt).getTime()))
                            .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0]?.sentAt;
                          
                          if (!latestSentAt) {
                            return 'No message provided';
                          }
                          
                          return new Date(latestSentAt).toLocaleDateString();
                        })()}</span>
                        <span>⏰ Deadline: {(() => {
                          if (!packageSubmissions || packageSubmissions.length === 0) {
                            return 'Not set';
                          }
                          
                          // Find the latest deadline from all submissions
                          const latestDeadline = packageSubmissions
                            .filter(sub => sub.submissionDeadline && !isNaN(new Date(sub.submissionDeadline).getTime()))
                            .sort((a, b) => new Date(b.submissionDeadline).getTime() - new Date(a.submissionDeadline).getTime())[0]?.submissionDeadline;
                          
                          if (!latestDeadline) {
                            return 'Not set';
                          }
                          
                          return new Date(latestDeadline).toLocaleDateString();
                        })()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Email Subject and Message aligned with title */}
                  <div className="flex-1 max-w-md space-y-3 ml-8">
                    {/* Email Subject with expandable Message to Recipients */}
                    {(() => {
                      if (!packageSubmissions || packageSubmissions.length === 0) {
                        return null;
                      }
                      
                      // Find the submission with the most complete data (email subject and message)
                      const bestSubmission = packageSubmissions
                        .filter(sub => sub.emailSubject && sub.emailSubject !== 'null' && sub.emailSubject.trim() !== '')
                        .sort((a, b) => {
                          // Prioritize submissions with sentAt (actually sent)
                          if (a.sentAt && !b.sentAt) return -1;
                          if (!a.sentAt && b.sentAt) return 1;
                          return 0;
                        })[0];
                      
                      if (!bestSubmission?.emailSubject) {
                        return null;
                      }
                      
                      return (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-start space-x-2">
                            <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-blue-700 mb-1">Email Subject:</div>
                              <div className="text-sm text-blue-600 mb-3">{bestSubmission.emailSubject}</div>
                              
                              {/* Message to Recipients - Expandable Button */}
                              {bestSubmission?.messageToRecipients && 
                               bestSubmission.messageToRecipients.trim() && 
                               bestSubmission.messageToRecipients !== 'No message provided' && (
                                <div>
                                  <button
                                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                    onClick={(e) => {
                                      const btn = e.currentTarget;
                                      const content = btn.nextElementSibling as HTMLElement;
                                      const icon = btn.querySelector('.expand-icon') as HTMLElement;
                                      
                                      if (content.style.display === 'none' || content.style.display === '') {
                                        content.style.display = 'block';
                                        icon.style.transform = 'rotate(180deg)';
                                      } else {
                                        content.style.display = 'none';
                                        icon.style.transform = 'rotate(0deg)';
                                      }
                                    }}
                                  >
                                    <span>Message to Recipients</span>
                                    <svg 
                                      className="expand-icon w-4 h-4 transition-transform duration-200" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  
                                  <div 
                                    className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 text-sm text-gray-600 whitespace-pre-wrap"
                                    style={{ display: 'none' }}
                                  >
                                    {bestSubmission.messageToRecipients}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>


                {/* Key Metrics */}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">

                  <div className="bg-white rounded-lg p-4 border border-gray-200">

                    <div className="flex items-center justify-between">

                      <div>

                        <p className="text-sm font-medium text-gray-600">Companies</p>

                        <p className="text-2xl font-bold text-gray-900">{[...new Set(getValidSubmissions(packageSubmissions, packageData?.isAddendum).map(s => s.recipientCompany))].length}</p>

                      </div>

                      <Building className="w-8 h-8 text-blue-600" />

                    </div>

                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">

                    <div className="flex items-center justify-between">

                      <div>

                        <p className="text-sm font-medium text-gray-600">Total Views</p>

                        <p className="text-2xl font-bold text-gray-900">

                          {packageSubmissions.reduce((sum, s) => sum + (s.viewCount || 0), 0)}

                        </p>

                      </div>

                      <Eye className="w-8 h-8 text-green-600" />

                    </div>

                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">

                    <div className="flex items-center justify-between">

                      <div>

                        <p className="text-sm font-medium text-gray-600">Downloads</p>

                        <p className="text-2xl font-bold text-gray-900">

                          {packageSubmissions.reduce((sum, s) => sum + (s.downloadCount || 0), 0)}

                        </p>

                      </div>

                      <Download className="w-8 h-8 text-purple-600" />

                    </div>

                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">

                    <div className="flex items-center justify-between">

                      <div>

                        <p className="text-sm font-medium text-gray-600">Viewed By</p>

                        <p className="text-2xl font-bold text-gray-900">

                          {packageSubmissions.filter(s => s.firstViewedAt).length}

                        </p>

                      </div>

                      <CheckCircle className="w-8 h-8 text-orange-600" />

                    </div>

                  </div>

                </div>



                {/* Company Details */}

                <div className="space-y-4">

                  <div className="flex items-center justify-between">

                    <h4 className="text-lg font-semibold text-gray-900">Company Details & Access</h4>

                    <div className="flex gap-2">

                      {/* Auto-sync addendum packages - Only for main packages */}

                      {!packageData?.isAddendum && packageSubmissions.length > 0 && (

                        <div className="flex items-center text-sm text-green-600">

                          <CheckCircle className="w-4 h-4 mr-2" />

                          <span>Addendums auto-synced</span>

                        </div>

                      )}

                      <Button

                        variant="outline"

                        size="sm"

                        onClick={() => setShowSubmitDialog(true)}

                        className="text-blue-600 border-blue-200 hover:bg-blue-50"

                      >

                        <Plus className="w-4 h-4 mr-2" />

                        {hasExistingSubmissions ? 'Add More Companies' : (packageData?.isAddendum ? 'Add Companies to Parent Package' : 'Submit Tender Package')}

                      </Button>

                    </div>

                  </div>

                  

                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">

                    {/* Group submissions by company and show one card per company */}

                    {Array.isArray(packageSubmissions) && Object.entries(

                      getValidSubmissions(packageSubmissions, packageData?.isAddendum).reduce((groups, submission) => {

                        const companyName = submission.recipientCompany;

                        if (!groups[companyName]) {

                          groups[companyName] = [];

                        }

                        groups[companyName].push(submission);

                        return groups;

                      }, {} as Record<string, typeof packageSubmissions>)

                    ).map(([companyName, companySubmissions]) => {
                      // Use isAddendum from packageData (simplified logic)
                      const isAddendumPackage = !!packageData?.isAddendum;
                      
                      // Aggregate data for the company

                      const totalViews = Array.isArray(companySubmissions) ? companySubmissions.reduce((sum, s) => sum + (s.viewCount || 0), 0) : 0;

                      const totalDownloads = Array.isArray(companySubmissions) ? companySubmissions.reduce((sum, s) => sum + (s.downloadCount || 0), 0) : 0;

                      const hasViewed = Array.isArray(companySubmissions) ? companySubmissions.some(s => s.firstViewedAt) : false;

                      const lastViewed = Array.isArray(companySubmissions) ? companySubmissions.reduce((latest, s) => {

                        if (!s.lastViewedAt) return latest;

                        if (!latest) return s.lastViewedAt;

                        return new Date(s.lastViewedAt) > new Date(latest) ? s.lastViewedAt : latest;

                      }, null as string | null) : null;

                      const accessCode = Array.isArray(companySubmissions) ? companySubmissions[0]?.companyAccessCode : '';

                      const userEmails = Array.isArray(companySubmissions) ? companySubmissions.map(s => s.recipientEmail).join(', ') : '';

                      // Check if any member has access (for company status)
                      const anyMemberHasAccess = Array.isArray(companySubmissions) ? 
                        companySubmissions.some(s => s.accessGranted === 'yes' || s.accessGranted === undefined) : true;


                      return (

                        <div key={companyName} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">

                          <div className="flex items-start justify-between mb-4">

                            <div className="flex-1 min-w-0">

                              <div className="font-semibold text-gray-900 truncate text-lg">{companyName}</div>

                              <div className="text-gray-600 text-sm truncate">{userEmails}</div>

                              <div className="text-xs text-gray-500 mt-1">{Array.isArray(companySubmissions) ? companySubmissions.length : 0} recipients</div>

                            </div>

                            <div className="ml-3 flex items-center gap-3">
                              {anyMemberHasAccess ? (
                                // 如果有任何成员仍有访问权限，显示 'X' 按钮以移除访问
                                // 对于子包，禁用删除按钮（数据从主包同步）
                                <button
                                  onClick={() => {
                                    // Don't allow company removal in addendum packages (synced from parent)
                                    if (isAddendumPackage) {
                                      toast({
                                        title: "Cannot remove company",
                                        description: "Cannot remove companies in addendum packages. These are synced from the main package.",
                                        variant: "destructive"
                                      });
                                      return;
                                    }
                                    setCompanyToRemove(companyName);
                                    setShowRemoveCompanyDialog(true);
                                  }}
                                  disabled={isAddendumPackage}
                                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors flex-shrink-0 border-2 ${
                                    isAddendumPackage 
                                      ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-50' 
                                      : 'bg-red-100 border-red-200 hover:bg-red-200 hover:border-red-300'
                                  }`}
                                  title={isAddendumPackage ? 'Cannot remove in addendum packages' : 'Remove company access'}
                                >
                                  <X className={`w-4 h-4 font-bold ${
                                    isAddendumPackage ? 'text-gray-500' : 'text-red-600'
                                  }`} />
                                </button>
                              ) : (
                                // 如果所有成员的访问权限都已移除，显示 '撤回' 按钮
                                // 对于子包，禁用撤回按钮（数据从主包同步）
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    // Don't allow company restore in addendum packages (synced from parent)
                                    if (isAddendumPackage) {
                                      toast({
                                        title: "Cannot revert",
                                        description: "Cannot revert company access changes in addendum packages. These are synced from the main package.",
                                        variant: "destructive"
                                      });
                                      return;
                                    }
                                    updateCompanyAccessMutation.mutate({
                                      packageId: actualPackageId,
                                      companyName,
                                      accessGranted: 'yes'
                                    });
                                  }}
                                  className={`px-2 py-1 h-8 w-8 flex-shrink-0 border-2 ${
                                    isAddendumPackage 
                                      ? 'border-gray-300 opacity-50' 
                                      : 'border-gray-300 hover:border-gray-400'
                                  }`}
                                  title={isAddendumPackage ? 'Cannot revert in addendum packages' : 'Restore company access'}
                                  disabled={isAddendumPackage || updateCompanyAccessMutation.isPending}
                                >
                                  {updateCompanyAccessMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>

                          </div>

                          

                          <div className="mb-4">

                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Access Code</div>

                            <div className="font-mono text-lg font-bold text-gray-900 bg-gray-50 px-3 py-2 rounded border">

                              {accessCode || 'N/A'}

                            </div>

                          </div>

                          

                          <div className="grid grid-cols-2 gap-3 mb-4">

                            <div className="text-center">

                              <div className="text-lg font-bold text-blue-600">{totalViews}</div>

                              <div className="text-xs text-gray-500">Views</div>

                            </div>

                            <div className="text-center">

                              <div className="text-lg font-bold text-purple-600">{totalDownloads}</div>

                              <div className="text-xs text-gray-500">Downloads</div>

                            </div>

                          </div>

                          

                          {lastViewed && (

                            <div className="text-xs text-gray-500 mb-3">

                              Last viewed: {new Date(lastViewed).toLocaleDateString()}

                            </div>

                          )}

                          

                          <Button

                            variant="outline"

                            size="sm"

                            className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"

                            onClick={() => {

                              // Set selected company for recipient management

                              setSelectedCompanyForManagement(companyName);

                              setShowCompanyRecipientsDialog(true);

                            }}

                          >

                            <User className="w-4 h-4 mr-2" />

                            View/Manage ({Array.isArray(companySubmissions) ? companySubmissions.length : 0} recipients)

                          </Button>

                          

                          <Button

                            variant="ghost"

                            size="sm"

                            className="w-full text-gray-600 hover:bg-gray-50 mt-2"

                            onClick={() => {

                              if (accessCode) {

                                navigator.clipboard.writeText(accessCode);

                                toast({

                                  title: "Access code copied",

                                  description: "Company access code copied to clipboard"

                                });

                              }

                            }}

                          >

                            <Copy className="w-4 h-4 mr-2" />

                            Copy Access Code

                          </Button>

                          

                          <Button

                            variant="ghost"

                            size="sm"

                            className="w-full text-gray-600 hover:bg-gray-50 mt-1"

                            onClick={() => {

                              toast({

                                title: "Activity Details",

                                description: "Activity timeline feature will be implemented next.",

                              });

                            }}

                          >

                            <Clock className="w-4 h-4 mr-2" />

                            View Activity Timeline

                          </Button>

                        </div>

                      );

                    })}

                  </div>

                </div>

              </div>

            ) : (

              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">

                <Send className="w-12 h-12 mx-auto text-gray-400 mb-4" />

                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Submissions Yet</h3>

                <p className="text-gray-500 mb-4">This package hasn't been submitted to any companies.</p>

                {!isPackageLocked && packageData?.items?.length > 0 && (

                  <Button

                    onClick={handleSubmitDialogOpen}

                    className="bg-green-600 hover:bg-green-700 text-white"

                  >

                    <Send className="w-4 h-4 mr-2" />

                    {packageData?.isAddendum ? 'Add Companies to Parent Package' : 'Submit Tender Package'}

                  </Button>

                )}

              </div>

            )}

          </TabsContent>



          {/* Tender Returns Tab */}

          <TabsContent value="returns" className="space-y-6">

            <TenderReturnsTab 

              packageId={actualPackageId} 

              addendumId={addendumId}

              packageSubmissions={packageSubmissions}

              processedAwards={processedAwards}

              tenderSettings={tenderSettings}

            />

          </TabsContent>



          {/* Analytics Tab */}

          <TabsContent value="analytics" className="space-y-6">

            <Card className="border-gray-200 shadow-sm">

              <CardHeader>

                <CardTitle className="flex items-center text-lg font-semibold text-gray-900">

                  <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />

                  Package Analytics & Insights

                </CardTitle>

              </CardHeader>

              <CardContent>

                {packageSubmissions.length > 0 ? (

                  <div className="space-y-6">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">

                        <div className="flex items-center justify-between">

                          <div>

                            <p className="text-sm font-medium text-blue-800">Engagement Rate</p>

                            <p className="text-2xl font-bold text-blue-900">

                              {Math.round((packageSubmissions.filter(s => s.firstViewedAt).length / packageSubmissions.length) * 100)}%

                            </p>

                          </div>

                          <Eye className="w-8 h-8 text-blue-600" />

                        </div>

                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">

                        <div className="flex items-center justify-between">

                          <div>

                            <p className="text-sm font-medium text-green-800">Avg Views</p>

                            <p className="text-2xl font-bold text-green-900">

                              {Math.round(packageSubmissions.reduce((sum, s) => sum + (s.viewCount || 0), 0) / packageSubmissions.length)}

                            </p>

                          </div>

                          <BarChart3 className="w-8 h-8 text-green-600" />

                        </div>

                      </div>

                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">

                        <div className="flex items-center justify-between">

                          <div>

                            <p className="text-sm font-medium text-purple-800">Download Rate</p>

                            <p className="text-2xl font-bold text-purple-900">

                              {Math.round((packageSubmissions.filter(s => s.downloadCount > 0).length / packageSubmissions.length) * 100)}%

                            </p>

                          </div>

                          <Download className="w-8 h-8 text-purple-600" />

                        </div>

                      </div>

                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">

                        <div className="flex items-center justify-between">

                          <div>

                            <p className="text-sm font-medium text-orange-800">Response Rate</p>

                            <p className="text-2xl font-bold text-orange-900">
                              {Math.round((packageSubmissions.filter(s => 
                                s.status === 'tender_returned' || 
                                s.status === 'awarded'
                              ).length / packageSubmissions.length) * 100)}%
                            </p>

                          </div>

                          <CheckCircle className="w-8 h-8 text-orange-600" />

                        </div>

                      </div>

                    </div>

                    

                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">

                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Company Activity Overview</h4>

                      <div className="space-y-3">

                        {packageSubmissions.map((submission, index) => (

                          <div key={index} className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">

                            <div className="flex items-center space-x-3">

                              <div className={`w-3 h-3 rounded-full ${

                                submission.firstViewedAt ? 'bg-green-500' : 'bg-gray-300'

                              }`}></div>

                              <div>

                                <div className="font-medium text-gray-900">{submission.recipientCompany}</div>

                                <div className="text-sm text-gray-500">{submission.recipientEmail}</div>

                              </div>

                            </div>

                            <div className="flex items-center space-x-4 text-sm">

                              <span className="text-blue-600">{submission.viewCount || 0} views</span>

                              <span className="text-purple-600">{submission.downloadCount || 0} downloads</span>

                              <span className={`px-3 py-1 rounded-full text-xs font-medium w-20 text-center ${

                                submission.firstViewedAt 

                                  ? 'bg-green-100 text-green-800' 

                                  : 'bg-gray-100 text-gray-600'

                              }`}>

                                {submission.firstViewedAt ? 'Active' : 'Not viewed'}

                              </span>

                            </div>

                          </div>

                        ))}

                      </div>

                    </div>

                  </div>

                ) : (

                  <div className="text-center py-12">

                    <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Analytics Data</h3>

                    <p className="text-gray-500">Submit the package to companies to see analytics and insights.</p>

                  </div>

                )}

              </CardContent>

            </Card>

          </TabsContent>

        </Tabs>





        {/* Add Files Dialog */}

        <Dialog open={showAddFilesDialog} onOpenChange={handleDialogOpenChange}>

          <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden bg-white border-gray-200">

            <DialogHeader className="pb-4 border-b border-gray-100">

              <DialogTitle className="text-xl font-semibold text-gray-900">Add Files to Package</DialogTitle>

              <DialogDescription className="text-gray-600 mt-2">

                Select files and folders from your project to add to this package

              </DialogDescription>

            </DialogHeader>

            

            <div className="flex flex-col h-[50vh]">

              {/* Hierarchical File Selector */}

              <HierarchicalFileSelector 

                onSelectionChange={setSelectedFiles}

                selectedFiles={selectedFiles}

                className="flex-1"

              />

            </div>



            <DialogFooter className="pt-4 border-t border-gray-100">

              <div className="flex items-center justify-between w-full">

                <p className="text-sm text-gray-600">

                  {selectedFiles.length} file(s) selected

                </p>

                <div className="flex space-x-2">

                  <Button 

                    variant="outline" 

                    onClick={() => setShowAddFilesDialog(false)}

                    disabled={addFilesMutation.isPending}

                    className="h-11 px-6 text-gray-700 border-gray-300 hover:bg-gray-50"

                  >

                    Cancel

                  </Button>

                  <Button 

                    onClick={handleAddFiles}

                    disabled={addFilesMutation.isPending || selectedFiles.length === 0}

                    className="h-11 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white border-0 shadow-sm"

                  >

                    {addFilesMutation.isPending ? (

                      <>

                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />

                        Adding Files...

                      </>

                    ) : (

                      <>

                        <Plus className="w-4 h-4 mr-2" />

                        Add {selectedFiles.length} File(s)

                      </>

                    )}

                  </Button>

                </div>

              </div>

            </DialogFooter>

          </DialogContent>

        </Dialog>



        {/* Submit Tender Dialog */}

        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>

          <DialogContent className="sm:max-w-4xl bg-white border-emerald-200 shadow-xl h-[700px] flex flex-col overflow-hidden p-0">
            
            <DialogHeader className="sr-only">
              <DialogTitle>
                {hasExistingSubmissions ? 'Add More Companies' : (packageData?.isAddendum ? 'Add Companies to Parent Package' : 'Submit Tender Package')}
              </DialogTitle>
              <DialogDescription>
                {hasExistingSubmissions 
                  ? 'Add additional companies to this tender package'
                  : packageData?.isAddendum 
                  ? 'Add companies to the parent package (submissions will be shared with this addendum)'
                  : 'Search and select companies from your project library'
                }
              </DialogDescription>
            </DialogHeader>

            <DialogTitle className="sr-only">
              {hasExistingSubmissions ? 'Add More Companies' : (packageData?.isAddendum ? 'Add Companies to Parent Package' : 'Submit Tender Package')}
            </DialogTitle>

            <DialogDescription className="sr-only">
              {hasExistingSubmissions 
                ? 'Add more companies to your existing tender submission'
                : (packageData?.isAddendum 
                  ? 'Search and select companies from your project library to submit this addendum package'
                  : 'Search and select companies from your project library to submit this tender package')}
            </DialogDescription>

            {/* Clean Header - No Background */}

            <div className="border-b border-gray-200 pt-3 pb-3 px-6 flex-shrink-0">

              <div className="flex items-center justify-between">

                <div className="flex items-center space-x-3">

                  <div className="p-1.5 bg-gray-100 rounded-lg">

                    <Package className="w-5 h-5 text-gray-700" />

                  </div>

                  <div>

                    <h2 className={`text-lg font-semibold ${packageData?.isAddendum ? 'text-purple-700' : 'text-gray-900'}`}>

                      {hasExistingSubmissions ? 'Add More Companies' : (packageData?.isAddendum ? 'Add Companies to Parent Package' : 'Submit Tender Package')}

                    </h2>

                    <p className="text-gray-600 text-sm">

                      {hasExistingSubmissions 

                        ? 'Add additional companies to this tender package'

                        : packageData?.isAddendum 

                        ? 'This addendum will be sent to the same companies as the main package'

                        : 'Search and select companies from your project library'

                      }

                    </p>

                  </div>

                </div>

                

                {/* Add Companies Link - Two Lines */}

                {!packageData?.isAddendum && (

                  <div className="text-right mr-8">

                    <div className="text-xs text-gray-500 mb-1">Need to add more companies or users?</div>

                    <Link 

                      href={`/companies/projects/${packageData?.projectId || packageData?.acc_project_id || 'undefined'}`}

                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 hover:text-white rounded-md border border-red-500 shadow-sm transition-colors"

                    >

                      <span>Go to Company Management</span>
                      <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>

                    </Link>

                  </div>

                )}

              </div>

            </div>

            

            {/* Content Area */}

            <div className="flex-1 overflow-y-auto bg-white">

              {/* Addendum Package Info - Clean Gray Card */}

              {packageData?.isAddendum ? (

                <div className="mx-4 mb-3">

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">

                    <div className="flex items-center justify-between">

                      <div className="flex items-center space-x-3">

                        <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center">

                          <Package className="w-4 h-4 text-purple-700" />

                        </div>

                        <div>

                          <h3 className="text-sm font-semibold text-gray-900">Addendum Package</h3>

                          <p className="text-xs text-gray-600">

                            Sent to same companies as main package

                          </p>

                        </div>

                      </div>

                      

                      {isLoadingAddendumData ? (

                        <div className="flex items-center space-x-2">

                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>

                          <span className="text-xs text-gray-600">Loading...</span>

                        </div>

                      ) : (

                        <div className="flex items-center space-x-6">

                          <div className="text-center">

                            <div className="text-lg font-bold text-gray-900">{selectedRecipients.length}</div>

                            <div className="text-xs text-gray-600">Recipients</div>

                          </div>

                          <div className="text-center">

                            <div className="text-lg font-bold text-gray-900">{selectedCompanies.length}</div>

                            <div className="text-xs text-gray-600">Companies</div>

                          </div>

                        </div>

                      )}

                    </div>

                  </div>

                </div>

              ) : (

                <>

                  <div className="mx-4 mb-3">

                    <div className="bg-white border border-gray-200 rounded-lg p-3">

                      <label className="block text-sm font-medium text-gray-700 mb-2">

                        Search Companies

                      </label>

                      <Input

                        type="text"

                        placeholder="Search by company name or trade..."

                        value={companySearchTerm}

                        onChange={(e) => setCompanySearchTerm(e.target.value)}

                        className="border-gray-300 focus:ring-gray-500 focus:border-gray-500 max-w-md"

                      />

                    </div>

                  </div>



                  {/* Company Selection Grid - Compact Cards */}

                  <div className="mx-4 mb-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">

                    {(libraryLoading || projectUsersLoading) ? (

                      <div className="text-center py-8">

                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>

                        <span className="text-gray-600 text-sm">Loading companies...</span>

                      </div>

                    ) : (

                      <div className="space-y-2">

                        {filteredCompanies.map((company) => {

                          const companyId = company.accId || company.id;

                          const users = company.users || [];

                          const isACC = company.source === 'acc';
                          const isDatabase = company.source === 'database';

                          

                          return (

                            <div 

                              key={companyId} 

                              className={`p-3 border rounded-md transition-all ${

                                selectedCompanies.includes(companyId)

                                  ? 'border-blue-500 bg-blue-50' 

                                  : isCompanyFullySubmitted(company)

                                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'

                                  : 'border-gray-200 hover:border-gray-300 cursor-pointer'

                              }`}

                              onClick={() => !isCompanyFullySubmitted(company) && handleCompanySelection(company)}

                            >

                              <div className="flex items-center space-x-3">

                                <Checkbox

                                  checked={selectedCompanies.includes(companyId)}

                                  disabled={isCompanyFullySubmitted(company)}

                                  className="border-gray-300"

                                />

                                <div className="flex-1">

                                  <div className="space-y-2">

                                    <div className="flex items-center justify-between">

                                      <div className="flex items-center space-x-2">

                                        <h4 className="text-sm font-medium text-gray-900">{company.name}</h4>

                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${

                                          isACC 

                                            ? 'bg-green-100 text-green-800' 

                                            : isDatabase
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-orange-100 text-orange-800'

                                        }`}>

                                          {isACC ? 'ACC' : isDatabase ? 'Database' : 'From Main Pack'}

                                        </span>

                                        {existingCompanyIds.includes(company.name) && (

                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">

                                            {isCompanyFullySubmitted(company) ? 'All Users Added' : 'Partially Added'}

                                          </span>

                                        )}

                                      </div>

                                      <span className="text-xs text-gray-500">{company.type || company.trade}</span>

                                    </div>

                                    

                                    {/* User Details - Compact List - Show available users */}

                                    {(() => {

                                      const availableUsers = getAvailableUsersForCompany(company);

                                      const totalUsers = users.length;

                                      const recipientCount = totalUsers - availableUsers.length;

                                      

                                      return (

                                        <>

                                          {availableUsers.length > 0 && (

                                            <div className="space-y-1">

                                              <div className="text-xs text-gray-500 mb-1">

                                                Available users ({availableUsers.length}/{totalUsers})

                                                {recipientCount > 0 && ` • ${recipientCount} already added`}

                                              </div>

                                              {availableUsers.slice(0, 3).map((user: any, idx: number) => (

                                                <div key={idx} className="flex items-center text-xs text-gray-600">

                                                  <span className="font-medium">{user.name || `${user.firstName} ${user.lastName}`}</span>

                                                  <span className="mx-1">•</span>

                                                  <span>{user.email}</span>

                                                  {user.jobTitle && (

                                                    <>

                                                      <span className="mx-1">•</span>

                                                      <span className="text-gray-500">{user.jobTitle}</span>

                                                    </>

                                                  )}

                                                </div>

                                              ))}

                                              {availableUsers.length > 3 && (

                                                <div className="text-xs text-gray-500">

                                                  +{availableUsers.length - 3} more available users

                                                </div>

                                              )}

                                            </div>

                                          )}

                                          

                                          {availableUsers.length === 0 && totalUsers > 0 && (

                                            <div className="text-xs text-gray-500">

                                              All {totalUsers} users already added as recipients

                                            </div>

                                          )}

                                          

                                          {totalUsers === 0 && (

                                            <div className="text-xs text-gray-500">No users available</div>

                                          )}

                                        </>

                                      );

                                    })()}

                                  </div>

                                </div>

                              </div>

                            </div>

                          );

                        })}

                      </div>

                    )}

                    

                    {!(libraryLoading || projectUsersLoading) && filteredCompanies.length === 0 && (

                      <div className="text-center py-8">

                        <p className="text-gray-500 text-sm">No companies found</p>

                        <Button

                          type="button"

                          variant="outline"

                          size="sm"

                          onClick={handleSyncCompanies}

                          className="mt-2 text-blue-600 border-blue-200 hover:bg-blue-50"

                        >

                          <RefreshCw className="w-4 h-4 mr-1" />

                          Sync from Autodesk

                        </Button>

                      </div>

                    )}

                  </div>

                </>

              )}



              {/* Compact Recipients List - Grouped by Company */}

              {selectedRecipients.length > 0 && (

                <div className="mx-4 mb-2">

                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">

                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">

                      <div className="flex items-center justify-between">

                        <h4 className="text-sm font-semibold text-gray-900">

                          Selected Recipients

                        </h4>

                        <div className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs font-medium">

                          {selectedRecipients.length} recipients

                        </div>

                      </div>

                    </div>

                    <div className="max-h-40 overflow-y-auto p-3">

                      {(() => {

                        // Group recipients by company

                        const groupedRecipients = selectedRecipients.reduce((acc, recipient) => {

                          const companyName = recipient.companyName || 'Unknown Company';

                          if (!acc[companyName]) {

                            acc[companyName] = [];

                          }

                          acc[companyName].push(recipient);

                          return acc;

                        }, {} as Record<string, typeof selectedRecipients>);



                        return Object.entries(groupedRecipients).map(([companyName, recipients]) => (

                          <div key={companyName} className="mb-3 last:mb-0">

                            <div className="text-xs font-medium text-gray-600 mb-2">{companyName}</div>

                            <div className="grid grid-cols-2 gap-2">

                              {(recipients as any[]).map((recipient, index) => {

                                const isACC = recipient.source === 'acc';
                                const isDatabase = recipient.source === 'database';

                                const isDraft = packageData?.submissionStatus === 'draft';

                                const allowRemoval = true;

                                // 子包不显示删除按钮
                                const isAddendum = packageData?.isAddendum === true;

                                const canRemove = !isAddendum && (isDraft || allowRemoval);

                                

                                return (

                                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">

                                    <div className="flex items-center space-x-2 min-w-0 flex-1">

                                      <div className="w-6 h-6 bg-gray-400 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0">

                                        {(recipient.name || recipient.email).charAt(0).toUpperCase()}

                                      </div>

                                      <div className="min-w-0 flex-1">

                                        <p className="font-medium text-gray-900 truncate">

                                          {recipient.name || recipient.email}

                                        </p>

                                      </div>

                                    </div>

                                    <div className="flex items-center space-x-1 flex-shrink-0">

                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${

                                        isACC 

                                          ? 'bg-green-100 text-green-700' 

                                          : isDatabase
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-amber-100 text-amber-700'

                                      }`}>

                                        {isACC ? 'ACC' : isDatabase ? 'Database' : 'From Main Pack'}

                                      </span>

                                      {/* Show delete button for Add More Companies or when removal is allowed */}
                                      {/* For addendum packages, don't allow removing "From Main Pack" recipients */}

                                      {(hasExistingSubmissions || canRemove) && !(packageData?.isAddendum && !isACC && !isDatabase) && (

                                        <Button

                                          type="button"

                                          variant="ghost"

                                          size="sm"

                                          onClick={() => removeRecipient(index)}

                                          className="text-red-600 hover:bg-red-50 h-5 w-5 p-0"

                                        >

                                          <X className="w-3 h-3" />

                                        </Button>

                                      )}

                                    </div>

                                  </div>

                                );

                              })}

                            </div>

                          </div>

                        ));

                      })()}

                    </div>

                  </div>

                </div>

              )}





              {/* Compact Form Section - Combined */}

              <div className="mx-4 mb-2">

                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">

                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">

                    <div className="flex items-center space-x-2">

                      <h4 className="text-base font-semibold text-gray-900">Submission Details</h4>

                      <span className="text-xs text-gray-500">•</span>

                      <p className="text-xs text-gray-500">Configure the submission parameters and message</p>

                    </div>

                  </div>

                  <div className="p-3 space-y-3">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                      <div className="space-y-2">

                        <label className="block text-sm font-medium text-gray-700">

                          Tender Return Deadline <span className="text-red-500">*</span>

                        </label>

                        <Input

                          type="date"

                          value={hasExistingSubmissions 

                            ? (existingSubmissionData?.submissionDeadline instanceof Date 

                                ? existingSubmissionData.submissionDeadline.toISOString().split('T')[0]

                                : existingSubmissionData?.submissionDeadline 
                                  ? new Date(existingSubmissionData.submissionDeadline).toISOString().split('T')[0]
                                  : '')

                            : submissionDeadline}

                          onChange={(e) => setSubmissionDeadline(e.target.value)}

                          disabled={hasExistingSubmissions}

                          className={`border-gray-300 focus:ring-gray-500 focus:border-gray-500 ${

                            hasExistingSubmissions ? 'bg-gray-100 cursor-not-allowed' : ''

                          }`}

                          required

                        />

                      </div>

                      <div className="space-y-2">

                        <label className="block text-sm font-medium text-gray-700">

                          Email Subject <span className="text-red-500">*</span>

                        </label>

                        <Input

                          type="text"

                          value={hasExistingSubmissions ? existingSubmissionData?.emailSubject || '' : emailSubject}

                          onChange={(e) => setEmailSubject(e.target.value)}

                          disabled={hasExistingSubmissions}

                          className={`border-gray-300 focus:ring-gray-500 focus:border-gray-500 ${

                            hasExistingSubmissions ? 'bg-gray-100 cursor-not-allowed' : ''

                          }`}

                          required

                        />

                      </div>

                    </div>

                    

                    <div className="space-y-2">

                      <label className="block text-sm font-medium text-gray-700">

                        Message to Recipients <span className="text-red-500">*</span>

                      </label>

                      <Textarea

                        value={hasExistingSubmissions ? existingSubmissionData?.messageToRecipients || '' : emailMessage}

                        onChange={(e) => setEmailMessage(e.target.value)}

                        disabled={hasExistingSubmissions}

                        className={`border-gray-300 focus:ring-gray-500 focus:border-gray-500 ${

                          hasExistingSubmissions ? 'bg-gray-100 cursor-not-allowed' : ''

                        }`}

                        rows={3}

                        placeholder="Enter your message to recipients..."

                        required

                      />

                    </div>

                  </div>

                </div>

              </div>

            </div>



            {/* Clean Footer - No Background */}

            <div className="flex-shrink-0 border-t border-gray-200 pt-2 pb-3 px-3">

              <div className="flex items-center justify-between">

                <div className="flex items-center space-x-4 text-sm text-gray-600">

                  <span><span className="font-medium">{selectedRecipients.length}</span> recipients</span>

                  <span><span className="font-medium">{selectedFiles.length}</span> files</span>

                </div>

                

                <div className="flex items-center space-x-3">

                  <Button

                    variant="outline"

                    onClick={() => setShowSubmitDialog(false)}

                    className="px-4 py-2 border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"

                  >

                    Cancel

                  </Button>

                  

                  <Button

                    onClick={handleSubmit}

                    disabled={(() => {

                      // If there are existing submissions, use the existing data for validation

                      // Otherwise, use the current state values

                      const effectiveSubmissionDeadline = hasExistingSubmissions 

                        ? (existingSubmissionData?.submissionDeadline instanceof Date 

                            ? existingSubmissionData.submissionDeadline.toISOString().split('T')[0]

                            : existingSubmissionData?.submissionDeadline || '')

                        : submissionDeadline;

                      const effectiveEmailMessage = hasExistingSubmissions 

                        ? existingSubmissionData?.messageToRecipients || ''

                        : emailMessage;

                      const effectiveEmailSubject = hasExistingSubmissions 

                        ? existingSubmissionData?.emailSubject || ''

                        : emailSubject;

                      

                      const disabled = selectedRecipients.length === 0 || 

                        (!hasExistingSubmissions && !effectiveSubmissionDeadline.trim()) || 

                        (!hasExistingSubmissions && !effectiveEmailSubject.trim()) || 

                        (!hasExistingSubmissions && !effectiveEmailMessage.trim()) || 

                        submitMutation.isPending ||

                        (hasExistingSubmissions && !hasNewCompaniesSelected);

                      

                      return disabled;

                    })()}

                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"

                  >

                    {submitMutation.isPending ? (

                      <>

                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>

                        Sending...

                      </>

                    ) : (

                      <>

                        <Send className="w-4 h-4 mr-2" />

                        {hasExistingSubmissions 

                          ? `Add ${selectedRecipients.length} Recipients` 

                          : `Submit to ${selectedRecipients.length} Recipients`

                        }

                      </>

                    )}

                  </Button>

                </div>

              </div>

            </div>

          </DialogContent>

        </Dialog>



        {/* Create Addendum Dialog */}

        <Dialog open={showAddendumDialog} onOpenChange={setShowAddendumDialog}>

          <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden bg-white border-gray-200">

            <DialogHeader>

              <DialogTitle className="text-xl font-semibold text-gray-900">Create Addendum Package</DialogTitle>

              <div className="flex items-center gap-3 mt-2">

                <div className="text-sm text-gray-600">

                  Linked to Package {packageData?.packageNumber || 'WP0001'}

                </div>

                <div className="text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1 rounded border">

                  Addendum Number: {(() => {

                    if (!packageData?.packageNumber) return 'WP0001-AD01';

                    

                    // Get existing addendums for this package

                    const existingAddendums = packageData.addendums || [];

                    const nextAddendumNumber = existingAddendums.length + 1;

                    const paddedNumber = nextAddendumNumber.toString().padStart(2, '0');

                    

                    return `${packageData.packageNumber}-AD${paddedNumber}`;

                  })()}

                </div>

              </div>

              <DialogDescription className="text-gray-600 mt-2">

                Create an additional package linked to this submission with package preparation tracking

              </DialogDescription>

            </DialogHeader>

            

            <div className="space-y-6">

              {/* Addendum Form */}

              <div className="space-y-4">

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">

                    Addendum Package Name

                  </label>

                  <Input

                    type="text"

                    value={addendumName}

                    onChange={(e) => setAddendumName(e.target.value)}

                    placeholder="Enter addendum package name..."

                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>

                

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">

                    Description

                  </label>

                  <Textarea

                    value={addendumDescription}

                    onChange={(e) => setAddendumDescription(e.target.value)}

                    placeholder="Describe the addendum..."

                    rows={4}

                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>



                {/* Next Steps */}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">

                  <h4 className="font-semibold text-amber-900 mb-2">Next Steps After Creation</h4>

                  <ul className="text-sm text-amber-800 space-y-1">

                    <li>• Add relevant documents to the addendum package</li>

                    <li>• Review and validate document versions</li>

                    <li>• Submit addendum to existing tender recipients</li>

                    <li>• Track addendum response and engagement</li>

                  </ul>

                </div>

              </div>

            </div>



            <DialogFooter className="pt-6 border-t border-gray-200">

              <div className="flex items-center justify-end w-full">

                <div className="flex space-x-3">

                  <Button

                    variant="outline"

                    onClick={() => setShowAddendumDialog(false)}

                    disabled={createAddendumMutation.isPending}

                    className="border-gray-300 text-gray-700 hover:bg-gray-50"

                  >

                    Cancel

                  </Button>

                  <Button

                    onClick={handleCreateAddendum}

                    disabled={!addendumName.trim() || createAddendumMutation.isPending}

                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"

                  >

                    {createAddendumMutation.isPending ? (

                      <>

                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />

                        Creating...

                      </>

                    ) : (

                      <>

                        <Plus className="w-4 h-4 mr-2" />

                        Create Addendum

                      </>

                    )}

                  </Button>

                </div>

              </div>

            </DialogFooter>

          </DialogContent>

        </Dialog>



        {/* Share Link Dialog */}

        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>

          <DialogContent className="sm:max-w-md">

            <DialogHeader>

              <DialogTitle className="flex items-center gap-2">

                <Share2 className="h-5 w-5 text-blue-600" />

                Share Package Download Link

              </DialogTitle>

              <DialogDescription>

                Share this link to allow others to download the package. The link expires in 24 hours.

              </DialogDescription>

            </DialogHeader>

            <div className="flex items-center space-x-2">

              <div className="grid flex-1 gap-2">

                <Input

                  id="share-link"

                  value={shareUrl}

                  readOnly

                  className="font-mono text-sm"

                />

              </div>

              <Button size="sm" className="px-3" onClick={copyToClipboard}>

                <Copy className="h-4 w-4" />

              </Button>

            </div>

            <div className="flex justify-end space-x-2">

              <Button variant="outline" onClick={() => setShareDialogOpen(false)}>

                Close

              </Button>

              <Button onClick={copyToClipboard}>

                <Copy className="mr-2 h-4 w-4" />

                Copy Link

              </Button>

            </div>

          </DialogContent>

        </Dialog>



        {/* Company-Specific Recipients Management Dialog */}

        <Dialog open={showCompanyRecipientsDialog} onOpenChange={setShowCompanyRecipientsDialog}>

          <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden bg-white border-gray-200 shadow-xl">

            <DialogHeader>

              <DialogTitle className="text-xl font-semibold text-gray-900">

                Manage Recipients - {selectedCompanyForManagement}

              </DialogTitle>

              <DialogDescription className="text-gray-600">

                Manage recipients for this specific company

              </DialogDescription>

            </DialogHeader>

            <div className="py-4 overflow-y-auto flex-1 min-h-0">

                <div className="space-y-4">

                {/* Company Info */}

                <div className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded-lg">

                  <div className="flex items-center space-x-3">

                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">

                      <Building className="w-4 h-4 text-blue-600" />

                    </div>

                    <div>

                      <div className="font-medium text-gray-900">{selectedCompanyForManagement}</div>

                      <div className="text-sm text-gray-600">

                        {getValidSubmissions(packageSubmissions, packageData?.isAddendum).filter(s => s.recipientCompany === selectedCompanyForManagement).length} recipients

                      </div>

                    </div>

                  </div>


                </div>

                

                {/* Recipients List */}

                <div className="space-y-3">

                  {getValidSubmissions(packageSubmissions, packageData?.isAddendum)

                    .filter(s => s.recipientCompany === selectedCompanyForManagement)

                    .map((submission, index) => (

                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">

                      <div className="flex items-center space-x-3">

                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">

                          <span className="text-white font-medium text-sm">

                            {submission.recipientEmail.charAt(0).toUpperCase()}

                          </span>

                        </div>

                        <div>

                          <div className="font-medium text-gray-900">{submission.recipientEmail}</div>

                          <div className="text-sm text-gray-600">

                            {(submission.viewCount > 0) ? 'Viewed' : 'Not viewed'} • 

                            {submission.viewCount || 0} views • 

                            {submission.downloadCount || 0} downloads

                          </div>

                          {submission.messageToRecipients && submission.messageToRecipients.trim() && (

                            <div className="text-xs text-gray-500 mt-1">

                              <span className="font-medium">Message:</span> {submission.messageToRecipients}

                            </div>

                          )}

                        </div>

                      </div>

                      <div className="flex items-center space-x-2">

                        {/* Status and Access Control */}
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            submission.accessGranted === 'no'
                              ? 'bg-red-100 text-red-800'
                              : ((submission.viewCount > 0) || (submission.downloadCount > 0))
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                          }`}>
                            {submission.accessGranted === 'no' 
                              ? 'Access Removed' 
                              : ((submission.viewCount > 0) || (submission.downloadCount > 0)) ? 'Active' : 'Pending'
                            }
                          </span>
                          
                          {/* Undo button for removed access */}
                          {/* For addendum packages, disable undo button for synced members from parent */}
                          {submission.accessGranted === 'no' && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={packageData?.isAddendum === true}
                              onClick={() => {
                                // Don't allow undo for addendum packages (synced from parent)
                                if (packageData?.isAddendum === true) {
                                  toast({
                                    title: "Cannot revert",
                                    description: "Cannot revert access changes in addendum packages. These are synced from the main package.",
                                    variant: "destructive"
                                  });
                                  return;
                                }
                                updateMemberAccessMutation.mutate({
                                  packageId: actualPackageId,
                                  memberEmail: submission.recipientEmail,
                                  accessGranted: 'yes'
                                });
                              }}
                              className={`px-2 py-1 h-8 w-8 ${packageData?.isAddendum === true ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={packageData?.isAddendum === true ? 'Cannot revert in addendum packages' : 'Revert access removal'}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        {/* Delete button with permission check and clear feedback */}

                        {(() => {

                          const isDraft = packageData?.submissionStatus === 'draft';

                          const allowRemoval = true;

                          const isTenderReturned = submission.status === 'tender_returned' || submission.companyReturned;

                          // 子包不显示删除按钮
                          const isAddendum = packageData?.isAddendum === true;

                          // If tenderSettings is not loaded yet, allow removal for draft packages
                          // Allow removal even if tender has been returned when setting is enabled
                          const canRemove = !isAddendum && (isDraft || allowRemoval);

                          // Only show delete button if access is not already removed
                          const showDeleteButton = canRemove && submission.accessGranted !== 'no';

                          


                          if (showDeleteButton) {

                            return (

                              <Button

                                variant="ghost"

                                size="sm"

                                onClick={() => {
                                  // Set the recipient to delete and open confirmation dialog
                                  setRecipientToDelete({ 
                                    email: submission.recipientEmail,
                                    company: submission.recipientCompany
                                  });
                                  setShowDeleteConfirmDialog(true);
                                }}

                                className="text-red-600 hover:bg-red-50"

                              >

                                <Trash2 className="w-4 h-4" />

                              </Button>

                            );

                          } else {

                            // If access is already removed, don't show delete button at all (show undo button instead)
                            if (submission.accessGranted === 'no') {
                              return null;
                            }

                            // Show disabled button with tooltip explanation

                            return (

                              <Button

                                variant="ghost"

                                size="sm"

                                disabled

                              onClick={() => {

                                const isTenderReturned = submission.status === 'tender_returned' || submission.companyReturned;

                                toast({

                                  title: "Cannot remove recipient",

                                  description: isDraft 

                                    ? "Package is in draft status but removal is not allowed."

                                    : "Cannot remove recipients after package submission. Contact administrator to enable this setting.",

                                  variant: "destructive"

                                });

                              }}

                              className="text-gray-400 cursor-not-allowed"

                              title={(() => {

                                return isDraft 

                                  ? "Removal not allowed in current settings"

                                  : "Cannot remove recipients after submission. Enable this setting in project tender settings.";

                              })()}

                              >

                                <Trash2 className="w-4 h-4" />

                              </Button>

                            );

                          }

                        })()}

                      </div>

                    </div>

                  ))}

                </div>

                </div>

            </div>

            

            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-auto !sticky-0">

              <Button

                variant="outline"

                onClick={() => setShowCompanyRecipientsDialog(false)}

              >

                Close

              </Button>

            </DialogFooter>

          </DialogContent>

        </Dialog>



        {/* Add Company Dialog */}

        <Dialog open={showAddCompanyDialog} onOpenChange={setShowAddCompanyDialog}>

          <DialogContent className="sm:max-w-2xl bg-white border-gray-200">

            <DialogHeader>

              <DialogTitle className="text-xl font-semibold text-gray-900">Add New Company</DialogTitle>

              <DialogDescription className="text-gray-600">

                Create a new company to send tender packages to

              </DialogDescription>

            </DialogHeader>

            

            <div className="space-y-4">

              <div className="grid grid-cols-2 gap-4">

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-1">

                    Company Name *

                  </label>

                  <Input

                    value={companyName}

                    onChange={(e) => setCompanyName(e.target.value)}

                    placeholder="Enter company name"

                    className="border-gray-300 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-1">

                    Company Type *

                  </label>

                  <Input

                    value={companyType}

                    onChange={(e) => setCompanyType(e.target.value)}

                    placeholder="e.g., Contractor, Consultant"

                    className="border-gray-300 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>

              </div>

              


              

              <div className="grid grid-cols-3 gap-3">

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-1">

                    Phone Number

                  </label>

                  <Input

                    value={companyPhone}

                    onChange={(e) => setCompanyPhone(e.target.value)}

                    placeholder="Phone number"

                    className="border-gray-300 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-1">

                    Contact Person

                  </label>

                  <Input

                    value={contactPerson}

                    onChange={(e) => setContactPerson(e.target.value)}

                    placeholder="Primary contact name"

                    className="border-gray-300 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-1">

                    Address

                  </label>

                  <Input

                    value={companyAddress}

                    onChange={(e) => setCompanyAddress(e.target.value)}

                    placeholder="Company address"

                    className="border-gray-300 focus:ring-blue-500 focus:border-blue-500"

                  />

                </div>

              </div>

            </div>

            

            <DialogFooter className="space-x-2">

              <Button

                variant="outline"

                onClick={() => setShowAddCompanyDialog(false)}

                className="border-gray-300 text-gray-700 hover:bg-gray-50"

              >

                Cancel

              </Button>

              <Button

                onClick={handleCreateCompany}

                disabled={createCompanyMutation.isPending || !companyName || !companyType}

                className="bg-blue-600 hover:bg-blue-700 text-white"

              >

                {createCompanyMutation.isPending ? (

                  <>

                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>

                    Creating...

                  </>

                ) : (

                  <>

                    <Plus className="w-4 h-4 mr-2" />

                    Create Company

                  </>

                )}

              </Button>

            </DialogFooter>

          </DialogContent>

        </Dialog>






        {/* Recipients Management Dialog */}

        <Dialog open={showRecipientsDialog} onOpenChange={setShowRecipientsDialog}>

          <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col bg-white border-gray-200 shadow-xl">

            <DialogHeader className="border-b border-gray-200 pb-4">

              <DialogTitle className="flex items-center gap-2 text-gray-900 text-xl font-semibold">

                👥 Manage Recipients

              </DialogTitle>

              <p className="text-gray-600 mt-2">

                View and manage all recipients for this tender package. You can remove individual recipients or add new ones.

              </p>

            </DialogHeader>

            

            <div className="mt-6 space-y-6 overflow-y-auto flex-1">

              {/* Current Recipients List */}

              <div className="space-y-4">

                <h3 className="text-lg font-medium text-gray-900">Current Recipients ({packageSubmissions.length})</h3>

                

                <div className="space-y-3 max-h-96 overflow-y-auto">

                  {packageSubmissions.map((submission, index) => (

                    <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">

                      <div className="flex-1">

                        <div className="flex items-center space-x-3">

                          <div className="font-medium text-gray-900">{submission.recipientCompany}</div>

                          <Badge className={getStatusColor(submission.firstViewedAt ? 'viewed' : 'sent')}>

                            {submission.firstViewedAt ? 'Viewed' : 'Sent'}

                          </Badge>

                        </div>

                        <div className="text-sm text-gray-600 mt-1">{submission.recipientEmail}</div>

                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">

                          <span>👁 {submission.viewCount || 0} views</span>

                          <span>⬇ {submission.downloadCount || 0} downloads</span>

                          <span>🔑 {submission.companyAccessCode}</span>

                        </div>

                      </div>

                      {(() => {

                        const isDraft = packageData?.submissionStatus === 'draft';

                        const allowRemoval = true;

                        const isTenderReturned = submission.status === 'tender_returned' || submission.companyReturned;

                        // 子包不显示删除按钮
                        const isAddendum = packageData?.isAddendum === true;

                        const canRemove = !isAddendum && (isDraft || allowRemoval) && !isTenderReturned;

                        

                        if (canRemove) {

                          return (

                            <Button

                              variant="outline"

                              size="sm"

                              onClick={async () => {

                                // Double-check permissions before API call

                                const currentIsDraft = packageData?.submissionStatus === 'draft';

                                const currentAllowRemoval = true;

                                const currentIsTenderReturned = submission.status === 'tender_returned' || submission.companyReturned;

                                const currentIsAddendum = packageData?.isAddendum === true;

                                const currentCanRemove = !currentIsAddendum && (currentIsDraft || currentAllowRemoval) && !currentIsTenderReturned;

                                

                                if (!currentCanRemove) {

                                  toast({

                                    title: "Cannot remove recipient",

                                    description: currentIsTenderReturned

                                      ? "Cannot remove recipient who has already submitted a tender return."

                                      : "Removal is not allowed after package submission.",

                                    variant: "destructive"

                                  });

                                  return;

                                }

                                

                                try {

                                  console.log('Attempting to remove submission access:', submission.id);

                                  const response = await apiRequest(`/api/tender-submissions/${submission.id}`, {

                                    method: 'DELETE'

                                  });

                                  console.log('Remove access response:', response);

                                  

                                  // Invalidate queries to refresh the UI
                                  const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
                                  queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });
                                  queryClient.invalidateQueries({ queryKey: ['package-submissions', actualPackageId] });

                                  toast({

                                    title: "Recipient removed",

                                    description: "Recipient has been removed from this package.",

                                  });

                                } catch (error: any) {

                                  console.error('Delete API error details:', {

                                    error: error,

                                    message: error.message,

                                    status: error.status,

                                    response: error.response

                                  });

                                  toast({

                                    title: "Error",

                                    description: `Failed to remove recipient: ${error.message || 'Unknown error'}`,

                                    variant: "destructive"

                                  });

                                }

                              }}

                              className="text-red-600 hover:bg-red-50 border-red-200"

                            >

                              <X className="w-4 h-4" />

                            </Button>

                          );

                        } else {

                          return (

                            <Button

                              variant="outline"

                              size="sm"

                              disabled

                              onClick={() => {

                                const isTenderReturned = submission.status === 'tender_returned' || submission.companyReturned;

                                toast({

                                  title: "Cannot remove recipient",

                                  description: isTenderReturned

                                    ? "Cannot remove recipient who has already submitted a tender return."

                                    : "Cannot remove recipients after package submission. This can be enabled in project settings if needed.",

                                  variant: "destructive"

                                });

                              }}

                              className="text-gray-400 border-gray-200 cursor-not-allowed"

                              title={(() => {

                                const isTenderReturned = submission.status === 'tender_returned' || submission.companyReturned;

                                return isTenderReturned

                                  ? "Cannot remove - tender has been returned"

                                  : "Cannot remove recipients after submission";

                              })()}

                            >

                              <X className="w-4 h-4" />

                            </Button>

                          );

                        }

                      })()}

                    </div>

                  ))}

                </div>

              </div>



              {/* Add New Recipients */}

              <div className="border-t border-gray-200 pt-6">

                <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Recipients</h3>

                <div className="flex gap-2">

                  <Button

                    onClick={() => {

                      setShowRecipientsDialog(false);

                      setShowSubmitDialog(true);

                    }}

                    variant="outline"

                    className="border-green-300 text-green-700 hover:bg-green-50"

                  >

                    <Plus className="w-4 h-4 mr-2" />

                    Add Companies & Users

                  </Button>

                </div>

              </div>

            </div>

            

            <DialogFooter className="mt-auto border-t border-gray-200 pt-4">

              <Button

                variant="outline"

                onClick={() => setShowRecipientsDialog(false)}

                className="border-gray-300 text-gray-700 hover:bg-gray-50"

              >

                Close

              </Button>

            </DialogFooter>

          </DialogContent>

        </Dialog>

        </div>

      </div>

      {/* Show All References Dialog */}
      <Dialog open={showAllReferencesDialog} onOpenChange={setShowAllReferencesDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">All References</DialogTitle>
            <DialogDescription className="text-gray-600">
              View all related packages and addendums
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {packageData?.isAddendum ? (
              // For addendum, show main package + all other addendums
              <div className="space-y-3">
                {/* Main Package */}
                <div className="p-4 border border-gray-200 rounded-lg bg-blue-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">Main Package</h4>
                      <p className="text-sm text-gray-600">
                        {packageData.packageNumber?.split('-')[0] || `WP${packageData.parentPackageId?.slice(-4)}`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setLocation(`/tender-packages/projects/${packageData.acc_project_id}/${packageData.parentPackageId}`);
                      }}
                    >
                      View
                    </Button>
                  </div>
                </div>

                {/* Other Addendums */}
                {packageData.addendums && packageData.addendums.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Other Addendums ({packageData.addendums.length})</h4>
                    <div className="space-y-2">
                      {packageData.addendums.map((addendum, index) => (
                        <div key={addendum.id} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{addendum.packageNumber}</p>
                              <p className="text-sm text-gray-600">{addendum.name || 'Addendum'}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setLocation(`/tender-packages/projects/${packageData.acc_project_id}/${addendum.id}`);
                              }}
                            >
                              View
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // For main package, show all addendums
              packageData?.addendums && packageData.addendums.length > 0 ? (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">All Addendums ({packageData.addendums.length})</h4>
                  <div className="space-y-2">
                    {packageData.addendums.map((addendum, index) => (
                      <div key={addendum.id} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{addendum.packageNumber}</p>
                            <p className="text-sm text-gray-600">{addendum.name || 'Addendum'}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setLocation(`/tender-packages/projects/${packageData.acc_project_id}/${addendum.id}`);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No references found
                </div>
              )
            )}
          </div>

          <DialogFooter className="border-t border-gray-200 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowAllReferencesDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Company Confirmation Dialog */}
      <Dialog open={showRemoveCompanyDialog} onOpenChange={setShowRemoveCompanyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <X className="w-4 h-4 text-red-600" />
              </div>
              Remove Company Access
            </DialogTitle>
            <DialogDescription className="text-gray-600 mt-2">
              Are you sure you want to remove access for <strong>{companyToRemove}</strong>?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">This action will:</p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-700">
                    <li>Remove access for all members in this company</li>
                    <li>Change their status to "Access Removed"</li>
                    <li>Make the company card appear grayed out</li>
                    <li>Members can be restored individually later</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveCompanyDialog(false);
                setCompanyToRemove(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (companyToRemove) {
                  updateCompanyAccessMutation.mutate({
                    packageId: actualPackageId,
                    companyName: companyToRemove,
                    accessGranted: 'no'
                  });
                }
                setShowRemoveCompanyDialog(false);
                setCompanyToRemove(null);
              }}
              disabled={updateCompanyAccessMutation.isPending}
            >
              {updateCompanyAccessMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove Access'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Recipient Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
        <AlertDialogContent hideOverlay className="z-[10002] border-4 border-red-600 shadow-2xl ring-4 ring-red-600/30">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{recipientToDelete?.email}</strong> from <strong>{recipientToDelete?.company}</strong>?
              <br /><br />
              This action will revoke their access to view or download package files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRecipientToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                if (!recipientToDelete) return;

                const currentIsDraft = packageData?.submissionStatus === 'draft';
                const currentAllowRemoval = true;
                const currentIsAddendum = packageData?.isAddendum === true;
                const currentCanRemove = !currentIsAddendum && (currentIsDraft || currentAllowRemoval);

                if (!currentCanRemove) {
                  toast({
                    title: "Cannot remove recipient",
                    description: "Removal is not allowed after package submission.",
                    variant: "destructive"
                  });
                  setShowDeleteConfirmDialog(false);
                  setRecipientToDelete(null);
                  return;
                }

                try {
                  await apiRequest(`/api/tender-packages/${actualPackageId}/members/${encodeURIComponent(recipientToDelete.email)}`, {
                    method: 'DELETE'
                  });

                  const packageIdToUse = isAddendumView ? addendumId : actualPackageId;
                  queryClient.invalidateQueries({ queryKey: ['package-submissions', packageIdToUse] });
                  queryClient.invalidateQueries({ queryKey: ['package-submissions', actualPackageId] });

                  toast({
                    title: "Recipient removed",
                    description: "Recipient has been removed from this package.",
                  });

                  setShowDeleteConfirmDialog(false);
                  setRecipientToDelete(null);
                } catch (error: any) {
                  console.error('Delete API error details:', {
                    error: error,
                    message: error.message,
                    status: error.status,
                    response: error.response
                  });

                  toast({
                    title: "Error",
                    description: `Failed to remove recipient: ${error.message || 'Unknown error'}`,
                    variant: "destructive"
                  });

                  setShowDeleteConfirmDialog(false);
                  setRecipientToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>

  );

}

